import {BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig} from '@angular/platform-browser';
import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, Injectable, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import * as Hammer from 'hammerjs';
import {DragulaModule} from 'ng2-dragula';
import {ClipboardModule} from 'ngx-clipboard';

import {CookieModule} from 'ngx-cookie';
import {DashboardRoutingModule} from './dashboard-routing.module';
import {DashboardComponent} from './dashboard/dashboard.component';
import {WebsitesComponent} from './websites/websites.component';
import {WebsitePromptComponent} from './websites/website-prompt/website-prompt.component';
import {VoiceRecordingComponent} from './websites/website-prompt/components/voice-recording/voice-recording.component';
import {WebsiteComponent} from './websites/website/website.component';
import {ImageOptimizerComponent} from './websites/website/image-optimizer/image-optimizer.component';
import {MainHeaderComponent} from './main-header/main-header.component';
import {ProfilePopupComponent} from './profile-popup/profile-popup.component';
import {AccountComponent} from './account/account.component';
import {AccountSettingsComponent} from './account/account-settings/account-settings.component';
import {BillingComponent} from './workspace/billing/billing.component';
import {SecuritySettingsComponent} from "./account/security/security-settings.component";
import {SubscriptionPlanComponent} from './workspace/subscription-plan/subscription-plan.component';
import {CancelSubscriptionComponent} from './workspace/cancel-subscription/cancel-subscription.component';
import {SettingsResetPasswordComponent} from './account/reset-password/settings-reset-password.component';
import {WebsiteItemComponent} from './websites/website-item/website-item.component';
import {WebsiteItemMenuComponent} from './websites/website-item-menu/website-item-menu.component';
import {WebsiteService} from "./websites/website.service";
import {LoginForgotComponent} from "./auth/login-forgot/login-forgot.component";
import {LoginComponent} from "./auth/login/login.component";
import {AuthService} from "./auth/auth.service";
import {UserService} from "./user/user.service";
import {RequesterService} from "./shared/services/requester/requester.service";
import {GlobalEventDispatcherService} from "./shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {RequestRepeaterService} from "./shared/services/request-repeater/request-repeater.service";
import {AuthGuard} from "./auth/auth.guard";
import {SetupOnboardingRedirectGuard} from "./auth/setup-onboarding-redirect.guard";
import {ProductService} from "./websites/website/product.service";
import {RouteDataService} from "./shared/services/route-data/route-data.service";
import {TitleService} from "./shared/services/title/title.service";
import {WebsiteResolver} from "./websites/website/website-resolver";
import {AddWebsiteComponent} from './add-website/add-website.component';
import {GravatarDirective} from './shared/directives/gravatar/gravatar.directive';
import {WpLinkDirective} from './shared/directives/wp-link/wp-link.directive';
import {ResetPasswordComponent} from './auth/reset-password/reset-password.component';
import {AddPaymentMethodComponent} from './workspace/billing/add-payment-method/add-payment-method.component';
import {PaymentMethodComponent} from './workspace/billing/payment-dialog/payment-method/payment-method.component';
import {MonthPipe} from "./shared/pipes/month.pipe";
import {BackupService} from "./websites/website/backup/backup.service";
import {UserStateGuard} from "./user/user-state.guard";
import {WebsiteGuard} from "./websites/website/website.guard";
import {SequenceGuard} from "./shared/guards/sequence.guard";
import {DurationPipe} from "./shared/pipes/duration.pipe";
import {
  HTTP_INTERCEPTORS,
  HttpClientJsonpModule,
  HttpClientModule,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport
} from '@angular/common/http';
import {FormDataInterceptor} from "./shared/interceptors/form-data.interceptor";
import {ServiceKeyInterceptor} from "./shared/interceptors/service-key.interceptor";
import {DbResponseInterceptor} from "./shared/interceptors/db-response.interceptor";
import {BlockerScreenComponent} from "./shared/components/blocker-screen/blocker-screen.component";
import {RefreshTokenInterceptor} from "./shared/interceptors/refresh-token.interceptor";
import {GoogleAnalyticsService} from "./shared/services/google-analytics/google-analytics.service";
import {GoogleAnalytics4Service} from "./shared/services/google-analytics-4/google-analytics-4.service";
import {ComodoTrustLogoDirective} from "./shared/directives/comodo-trust-logo/comodo-trust-logo.directive";
import {DashboardBootstrapModalModule} from "./dashboard-bootstrap-modal.module";
import {environment} from "../environments/environment";
import {SeoComponent} from "./websites/website/seo/seo.component";
import {PerformanceService} from "./websites/website/performance/performance.service";
import {PageLoadingBarService} from './shared/services/page-loading-bar/page-loading-bar.service';
import {TextUtils} from './shared/utils/text-utils';
import {UserResolver} from './user/user-resolver';
import {PluginsResolver} from './websites/website/plugins/plugins-resolver';
import {AvailablePluginsComponent} from './websites/website/plugins/available-plugins/available-plugins.component';
import {ThemesResolver} from './websites/website/themes/themes-resolver';
import {AvailableThemesComponent} from './websites/website/themes/available-themes/available-themes.component';
import {PaymentMethodsResolver} from './workspace/billing/payment-methods-resolver';
import {PaymentsResolver} from './workspace/billing/payments-resolver';
import {UserSubscriptionResolver} from './workspace/subscription-plan/user-subscription-resolver';
import {UserPaymentMethodResolver} from './workspace/subscription-plan/user-payment-method-resolver';
import {ManagerPluginResolver} from './websites/website/manager-plugin-resolver';
import {ImageOptimizerPluginResolver} from './websites/website/image-optimizer-plugin-resolver';
import {BackupPluginResolver} from './websites/website/backup-plugin-resolver';
import {BackupsResolver} from './websites/website/backup/backups-resolver';
import {SeoPluginResolver} from './websites/website/seo-plugin-resolver';
import {EmailPreferencesComponent} from './account/email-preferences/email-preferences.component';
import {EmailPreferencesResolver} from './account/email-preferences/email-preferences-resolver';
import {WebsiteSidebarComponent} from "./website-sidebar/website-sidebar.component";
import {DbTextPipe} from './shared/pipes/db-text.pipe';
import {SizePipe} from './shared/pipes/size.pipe';
import {ReplaceCommas} from './shared/pipes/db-number-commas.pipe';
import {DbDatePipe} from './shared/pipes/db-date.pipe';
import {SortByPipe} from './shared/pipes/sort-by.pipe';
import {FilterByPropertyPipe} from './shared/pipes/filter-by-property.pipe';
import {SearchByPropertyPipe} from './shared/pipes/search-by-property.pipe';
import {PaginationPipe} from './shared/pipes/pagination.pipe';
import {LimitPipe} from './shared/pipes/limit.pipe';
import {LoadingSpinnerComponent} from './shared/components/loading-spinner/loading-spinner.component';
import {
  LoadingCircularSpinnerComponent
} from './shared/components/loading-circular-spinner/loading-circular-spinner.component';
import {LoadingBarComponent} from './shared/components/loading-bar/loading-bar.component';
import {PageLoadingBarComponent} from './shared/services/page-loading-bar/page-loading-bar.component';
import {LoadingScreenComponent} from './shared/components/loading-screen/loading-screen.component';
import {ProgressSpinnerComponent} from './shared/components/progress-spinner/progress-spinner.component';
import {RatingStarsComponent} from './shared/components/rating-stars/rating-stars.component';
import {WaitingSpinnerComponent} from './shared/components/waiting-spinner/waiting-spinner.component';
import {ChangelogComponent} from './websites/website/changelog/changelog.component';
import {DialogWrapperComponent} from './shared/components/dialog-wrapper/dialog-wrapper.component';
import {TooltipDirective} from './shared/directives/tooltip/tooltip.directive';
import {ImageZoomDirective} from './shared/directives/image-zoom/image-zoom.directive';
import {ImageZoomComponent} from './shared/directives/image-zoom/image-zoom.component';
import {FcRadioButtonComponent} from './shared/components/form-controls/radio-button/fc-radio-button.component';
import {SortProductsByParentProductPipe} from './websites/website/sort-products-by-parent-product.pipe';
import {TabsComponent} from './shared/components/tabs/tabs.component';
import {ActionProgressComponent} from './action-manager/action-progress/action-progress.component';
import {PageNotFoundComponent} from './page-not-found/page-not-found.component';
import {ThemesComponent} from './websites/website/themes/themes.component';
import {RouteWrapperComponent} from './shared/components/route-wrapper/route-wrapper.component';
import {DropdownListComponent} from './shared/components/dropdown-list/dropdown-list.component';
import {FtpInvalidDialogComponent} from './websites/website/ftp-invalid-dialog/ftp-invalid-dialog.component';
import {FilterProductByChildrenProductPipe} from './websites/website/filter-product-by-children.pipe';
import {TabHeadComponent} from './shared/components/tabs/tab-head/tab-head.component';
import {TabSubHeadComponent} from './shared/components/tabs/tab-subhead/tab-subhead.component';
import {PixelRatioDirective} from './shared/directives/pixel-ratio/pixel-ratio.directive';
import {ExtensionsComponent} from './websites/website/extensions/extensions.component';
import {
  FcDropdownSelectComponent
} from './shared/components/form-controls/dropdown-select/fc-dropdown-select.component';
import {SortIconComponent} from './shared/components/sort-icon/sort-icon.component';
import {ProgressBarComponent} from './shared/components/progress-bar/progress-bar.component';
import {NotifierComponent} from './shared/components/notifier/notifier.component';
import {PaginationControlsComponent} from './shared/components/pagination-controls/pagination-controls.component';
import {ActionManagerComponent} from './action-manager/action-manager.component';
import {TabContentComponent} from './shared/components/tabs/tab-content/tab-content.component';
import {AccordionComponent} from './shared/components/accordion/accordion.component';
import {PluginsComponent} from './websites/website/plugins/plugins.component';
import {FilterProductsByStatePipe} from './websites/website/filter-products-by-state.pipe';
import {
  AccordionItemHeadComponent
} from './shared/components/accordion/accordion-item/accordion-item-head/accordion-item-head.component';
import {ExtensionItemComponent} from './websites/website/extension-item/extension-item.component';
import {
  AccordionItemContentComponent
} from './shared/components/accordion/accordion-item/accordion-item-content/accordion-item-content.component';
import {FcSearchInputComponent} from './shared/components/form-controls/search-input/fc-search-input.component';
import {ConfirmDialogComponent} from './shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import {WebsiteCreationStatesDialogComponent} from './shared/components/dialogs/website-creation-loading/website-creation-states-dialog.component';
import {SortProductsByFreeInstalledPipe} from './websites/website/sort-products-by-free-installed.pipe';
import {ProductItemComponent} from './websites/website/product-item/product-item.component';
import {TooltipComponent} from './shared/directives/tooltip/tooltip.component';
import {ActionItemComponent} from './action-manager/action-item/action-item.component';
import {AccordionItemComponent} from './shared/components/accordion/accordion-item/accordion-item.component';
import {DropdownListDirective} from "./shared/components/dropdown-list/dropdown-list-directive";
import {EmptySearchComponent} from "./shared/components/empty-search/empty-search.component";
import {NouisliderModule} from 'ng2-nouislider'; // Temporarily re-added for build
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {TimeFromNowPipe} from "./shared/pipes/time-from-now.pipe";
import {
  AvailablePluginComponent
} from "./websites/website/plugins/available-plugins/available-plugin/available-plugin.component";
import {
  AvailableThemeComponent
} from "./websites/website/themes/available-themes/available-theme/available-theme.component";

import {MainHeaderContentDirective} from './main-header/main-header-content.directive';
import {MainHeaderService} from './main-header/main-header.service';
import {CustomScrollbarDirective} from './shared/directives/custom-scrollbar/custom-scrollbar.directive';
import {UpgradePlanComponent} from "./workspace/upgrade-plan/upgrade-plan.component";
import {FcSwitchComponent} from "./shared/components/form-controls/switch/fc-switch.component";
import {UserSubscriptionsResolver} from './workspace/upgrade-plan/user-subscriptions-resolver';
import {SubscriptionUpgradeAvailableGuard} from "./workspace/upgrade-plan/subscription-upgrade-available.guard";
import {PremiumPluginDialogComponent} from "./websites/website/premium-plugin-dialog/premium-plugin-dialog.component";
import {StripTagsPipe} from "./shared/pipes/stripTags.pipe";
import {RemovePaymentMethodComponent} from "./workspace/billing/remove-payment-method/remove-payment-method.component";
import {
  InactiveWebsiteTroubleshootComponent
} from "./websites/inactive-webiste-troubleshoot/inactive-website-troubleshoot.component";
import {UpgradeBannerComponent} from "./workspace/upgrade/upgrade-banner/upgrade-banner.component";
import {PeriodSwitchComponent} from "./workspace/upgrade/period-switch/period-switch.component";
import {DomainsSwitchComponent} from "./workspace/upgrade/domains-switch/domains-switch.component";
import {PlanItemComponent} from "./workspace/upgrade/plan-item/plan-item.component";
import {ServiceUpgradeComponent} from "./workspace/upgrade/service-upgrade/service-upgrade.component";
import {
  AccountUpgradedDialogComponent
} from "./shared/components/dialogs/account-upgraded-dialog/account-upgraded-dialog.component";
import {ContactUsComponent} from "./contact-us/contact-us.component";
import {WordByCountPipe} from "./shared/pipes/word-by-count.pipe";
import {AddZeroesPipe} from "./shared/pipes/add-zeroes.pipe";
import {TrimPipe} from "./shared/pipes/trim.pipe";
import {PaymentDialogComponent} from "./workspace/billing/payment-dialog/payment-dialog.component";
import {PaymentMethodItemComponent} from "./workspace/billing/payment-method-item/payment-method-item.component";
import {ActivityNotificationsComponent} from "./activity-notifications/activity-notifications.component";
import {ActivityNotificationService} from "./activity-notifications/activity-notification.service";
import {
  ActivityNotificationComponent
} from "./activity-notifications/activity-notification/activity-notification.component";

import {UserSummariesComponent} from "./user-summaries/user-summaries.component";
import {UserSummaryComponent} from "./user-summaries/user-summary/user-summary.component";

import {DbStoreModule} from "./store/db-store.module";
import {StoreModule} from '@ngrx/store';
import * as fromStore from './store';
import {EffectsModule} from '@ngrx/effects';
import {AvailableSubscriptionsResolver} from "./user/available-subscriptions-resolver";

import {OverviewComponent} from "./websites/website/overview/overview.component";
import {PluginsWidgetComponent} from "./websites/website/overview/plugins-widget/plugins-widget.component";
import {
  WidgetProductItemComponent
} from "./websites/website/overview/plugins-widget/product-item/widget-product-item.component";

import {CountPipe} from "./shared/pipes/count.pipe";
import {BackupLimitComponent} from "./websites/website/backup/backup-limit/backup-limit.component";
import {HostingComponent} from "./websites/website/hosting/hosting.component";
import {HostingInfoComponent} from "./websites/website/hosting/info/hosting-info.component";
import {HostingService} from "./websites/website/hosting/hosting.service";
import {HostingInfoResolver} from "./websites/website/hosting/info/hosting-info-resolver";
import {
  PushToLiveConfirmationComponent
} from "./websites/website/hosting/manage-environment/push-to-live/push-to-live-confirmation.component";
import {InfoTooltipDirective} from "./shared/components/info-tooltip/info-tooltip-directive";
import {InfoTooltipComponent} from "./shared/components/info-tooltip/info-tooltip.component";
import {OverviewOrderingResolver} from "./websites/website/overview/overview-ordering-resolver";
import {
  GeneratePasswordComponent
} from "./websites/website/hosting/info/generate-password-dialog/generate-password.component";
import {BackupDataLimitResolver} from "./websites/website/backup/backup-data-limit-resolver";
import {ProductActionService} from "./websites/website/product-action.service";
import {AddWebsiteDialogComponent} from "./websites/add-website-dialog/add-website-dialog.component";
import {AddWebsiteChoiceComponent} from "./websites/add-website-dialog/add-website-choice/add-website-choice.component";
import {InstallWebsiteComponent} from "./websites/add-website-dialog/install-website/install-website.component";
import {
  WebsiteActionSuccessComponent
} from "./websites/add-website-dialog/website-action-success/website-action-success.component";
import {
  MigrationManualStandardComponent
} from "./websites/add-website-dialog/migration-manual-standard/migration-manual-standard.component";
import {
  MigrationProgressComponent
} from "./websites/add-website-dialog/migration-progress/migration-progress.component";
import {HostingDomainsComponent} from "./websites/website/hosting/domains/hosting-domains.component";
import {HostingDomainsResolver} from "./websites/website/hosting/domains/hosting-domains-resolver";
import {
  HostingDomainsManageAllComponent
} from "./websites/website/hosting/manage-all-domains/hosting-domains-manage-all.component";
import {HostingToolsComponent} from "./websites/website/hosting/tools/hosting-tools.component";
import {
  ChangePhpEngineComponent
} from "./websites/website/hosting/tools/change-php-engine-dialog/change-php-engine.component";
import {EnableHttpsComponent} from "./websites/website/hosting/tools/enable-https-dialog/enable-https.component";
import {
  HostingDomainsManageDNSComponent
} from "./websites/website/hosting/manage-dns/hosting-domains-manage-dns.component";
import {
  HostingDomainsManageDnsResolver
} from "./websites/website/hosting/manage-dns/hosting-domains-manage-dns-resolver";
import {DomainItemComponent} from "./websites/website/hosting/domains/domain-item/domain-item.component";
import {HostingRedirectsComponent} from "./websites/website/hosting/redirects/hosting-redirects.component";
import {HostingRedirectsResolver} from "./websites/website/hosting/redirects/hosting-redirects-resolver";
import {
  UploadCsvDialogComponent
} from "./websites/website/hosting/redirects/upload-csv-dialog/upload-csv-dialog.component";
import {
  RemoveRedirectDialogComponent
} from "./websites/website/hosting/redirects/remove-redirect-dialog/remove-redirect-dialog.component";
import {
  RemoveDnsDialogComponent
} from "./websites/website/hosting/manage-dns/remove-dns-dialog/remove-dns-dialog.component";
import {
  UnavailableDialogComponent
} from "./websites/website/plugins/plugin-download-dialog/unavailable-dialog.component";
import {ProductStatusActionService} from "./websites/website/product-status-action.service";
import {HostingLogsComponent} from "./websites/website/hosting/logs/hosting-logs.component";
import {HostingLogsResolver} from "./websites/website/hosting/logs/hosting-logs-resolver";
import {SecurityService} from './websites/website/security/security.service';
import {SecurityScanResolver} from './websites/website/security/security-scan-resolver';
import {
  RestoreSecurityIssueDialogComponent
} from "./websites/website/security/restore-security-issue-dialog/restore-security-issue-dialog.component";
import {SecurityPluginResolver} from './websites/website/security-plugin-resolver';
import {SecurityIgnoredIssuesResolver} from './websites/website/security/security-ignored-issues-resolver';
import {PaypalButtonDirective} from './shared/directives/paypal-button/paypal-button.directive';
import {
  RemoveCertificateComponent
} from "./websites/website/hosting/tools/remove-certificate/remove-certificate.component";
import {CustomHttpsComponent} from "./websites/website/hosting/tools/custom-https-dialog/custom-https.component";
import {
  HostingResourceUsageByWebsiteResolver
} from "./websites/website/hosting/resource-usage/hosting-resource-usage-by-website-resolver";
import {
  HostingResourceUsageComponent
} from "./websites/website/hosting/resource-usage/hosting-resource-usage.component";
import {
  WordpressInstallationComponent
} from "./websites/add-website-dialog/wordress-installation/wordpress-installation.component";
import {ChangeEnvironmentComponent} from './websites/website/change-environment/change-environment.component';
import {RouteUrlHistoryService} from './shared/services/route-url-history/route-url-history.service';
import {
  HostingResourceVisitorsByWebsiteResolver
} from "./websites/website/hosting/resource-usage/hosting-resource-visitors-by-website-resolver";
import {HostingResourceUsageResolver} from "./websites/website/hosting/resource-usage/hosting-resource-usage-resolver";
import {
  HostingResourceVisitorsResolver
} from "./websites/website/hosting/resource-usage/hosting-resource-visitors-resolver";
import {
  HostingVisitsByWebsiteByDateResolver
} from "./websites/website/hosting/resource-usage/hosting-visits-by-website-by-date-resolver";
import {SecurityComponent} from './websites/website/security/security.component';
import {
  HostingDomainsManageAllResolver
} from "./websites/website/hosting/manage-all-domains/hosting-domains-manage-all-resolver";
import {HostingCertificateResolver} from "./websites/website/hosting/info/hosting-certificate-resolver";
import {
  AutomaticMigrationComponent
} from "./websites/add-website-dialog/automatic-migration/automatic-migration.component";
import {
  TxtContentDnsDialogComponent
} from "./websites/website/hosting/manage-dns/txt-content-dns-dialog/txt-content-dns-dialog.component";
import {UpgradePlanItemComponent} from './workspace/upgrade-plan/upgrade-plan-item/upgrade-plan-item.component';
import {PlanPeriodSwitchComponent} from './workspace/upgrade/plan-period-switch/plan-period-switch.component';
import {UserHostingSubscriptionsResolver} from './workspace/upgrade-plan/user-hosting-subscriptions-resolver';
import {SignUpComponent} from "./auth/sign-up/sign-up.component";
import {FromManagerDialogComponent} from "./websites/from-manager-dialog/from-manager-dialog.component";
import {SignUpGuard} from './auth/sign-up.guard';
import {DbNumberParseIntPipe} from "./shared/pipes/db-number-paresInt.pipe";
import {
  ConfirmDomainDeleteDialogComponent
} from "./websites/website/hosting/domains/domain-item/confirm-domain-delete-dialog/confirm-domain-delete-dialog.component";
import {
  ChangeEnvironmentDialogComponent
} from './websites/website/change-environment-dialog/change-environment-dialog.component';
import {WebsiteSettingsResolver} from './websites/website/website-settings-resolver';
import {
  PremiumPluginUpgradePlanItemComponent
} from "./websites/website/premium-plugin-dialog/premium-plugin-upgrade-plan-item/premium-plugin-upgrade-plan-item.component";
import {
  BundleUpgradePlanItemComponent
} from "./workspace/upgrade-plan/bundle-upgrade-plan-item/bundle-upgrade-plan-item.component";
import {HostingOfferBannerComponent} from './websites/hosting-offer-banner/hosting-offer-banner.component';
import {
  PgWelcomePerformanceDialogComponent
} from './websites/pg-welcome-performance-dialog/pg-welcome-performance-dialog.component';
import {
  HostingManageEnvironmentComponent
} from './websites/website/hosting/manage-environment/hosting-manage-environment.component';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {BreakTextsPipe} from "./shared/pipes/break-texts.pipe";
import {
  MigrationManualWordpressComponent
} from "./websites/add-website-dialog/migration-manual-wordpress/migration-manual-wordpress.component";
import {
  MigrationSubdomainHostingComponent
} from "./websites/add-website-dialog/migration-subdomain-hosting/migration-subdomain-hosting.component";
import {BuilderTemplatesService} from "./websites/website/builder-templates/builder-templates.service";
import {ElementorPluginResolver} from "./websites/website/builder-templates/elementor-plugin-resolver";
import {ImportTemplateComponent} from "./websites/website/builder-templates/import-template/import-template.component";
import {ScheduleComponent} from "./shared/components/schedule-block/schedule.component";
import {
  ProductItemScheduleComponent
} from "./websites/website/plugins/product-item-schedule/product-item-schedule.component";
import {
  WebsiteCopyCongratsDialogComponent
} from "./websites/add-website-dialog/website-copy-congrats-dialog/website-copy-congrats-dialog.component";
import {TenWebXComponent} from "./websites/website/tenWebX/tenWebX.component";
import {BuilderPluginResolver} from "./websites/website/builder-templates/builder-plugin-resolver";
import {LastImportedTemplateResolver} from "./websites/website/builder-templates/last-imported-template-resolver";
import {PaypalWaitingComponent} from "./paypal-waiting/paypal-waiting.component";
import {
  CouponApplyResponseDialogComponent
} from "./workspace/subscription-plan/coupon-apply-respoonse-dialog/coupon-apply-response-dialog.component";
import {GhostWaitingComponent} from "./ghost-waiting/ghost-waiting.component";
import {NgxCaptchaModule} from "ngx-captcha";
import {NgxChartsModule} from '@swimlane/ngx-charts'; // Temporarily re-added for build
import {NgChartsModule} from 'ng2-charts';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatNativeDateModule} from '@angular/material/core';
import {MatButtonModule} from '@angular/material/button';
import {AsyncPipe, CommonModule, DatePipe} from '@angular/common';
import {UserHostingServicesGuard} from "./user/user-hosting-services.guard";
import {InfoTooltipHoverComponent} from "./shared/components/info-tooltip-hover/info-tooltip-hover.component";
import {InfoTooltipHoverDirective} from "./shared/components/info-tooltip-hover/info-tooltip-hover-directive";
import {UserPlanTypeGuard} from "./user/user-plan-type.guard";
import {MySitesComponent} from "./websites/website/my-sites/mySites.component";
import {
  ThirdPartyThemeActivationDialogComponent
} from "./websites/website/themes/third-party-theme-activation-dialog/third-party-theme-activation-dialog.component";
import {Enable2faComponent} from "./account/security/enable-2fa/enable-2fa.component";
import {Disable2faComponent} from "./account/security/disable-2fa/disable-2fa.component";
import {VideoDialogComponent} from "./shared/components/dialogs/video-dialog/video-dialog.component";
import {DbSafePipe} from "./shared/pipes/db-set-safe.pipe";
import {HowToVideosComponent} from "./shared/components/how-to-videos/how-to-videos.component";
import {TwoFaVerificationComponent} from "./auth/2fa-verification/twoFa-verification.component";
import {
  MigrationManualSubdomainComponent
} from "./websites/add-website-dialog/migration-manual-subdomain/migration-manual-subdomain.component";
import {StarterGuard} from "./auth/starter.guard";
import {IssueItemComponent} from "./websites/website/security/issue-item/issue-item.component";
import {SignUpTrialGuard} from "./auth/sign-up-trial.guard";
import {SignUpTrialComponent} from "./auth/sign-up-trial/sign-up-trial.component";
import {WebsiteItemListViewComponent} from "./websites/website-item-list-view/website-item-list-view.component";
import {AddTagDialogComponent} from "./websites/tag-managment-dialogs/add-tag-dialog/add-tag-dialog.component";
import {TagsListDialogComponent} from "./websites/tag-managment-dialogs/tags-list-dialog/tags-list-dialog.component";
import {RemoveTagDialogComponent} from "./websites/tag-managment-dialogs/remove-tag-dialog/remove-tag-dialog.component";
import {NgMultiSelectDropDownModule} from "ng-multiselect-dropdown"; // Moved to feature modules
import {RemoveAllBackupsComponent} from "./websites/website/backup/remove-all-backups/remove-all-backups.component";
import {MoveTo10WebComponent} from "./websites/website/move-to-10web/moveTo10Web.component";
import {ChangePaymentMethodComponent} from "./workspace/billing/change-payment-method/change-payment-method.component";
import {PaymentHistoryComponent} from "./workspace/payment-history/payment-history.component";
import {
  AddWebsiteManuallyComponent
} from "./websites/add-website-dialog/add-website-manually/add-website-manually.component";
import {ChangeEmailComponent} from "./account/change-email/change-email.component";
import {ChangeEmailSuccessComponent} from "./account/change-email-success/change-email-success.component";
import {ResetPasswordSuccessComponent} from "./account/reset-password-success/reset-password-success.component";
import {
  MultisiteActionConfirmDialogComponent
} from "./shared/components/dialogs/mulitiste-action-confirm-dialog/multisite-action-confirm-dialog.component";
import {
  GoogleInsightsScoreDialogComponent
} from "./shared/components/dialogs/google-insights-score-dialog/google-insights-score-dialog.component";
import {AddDnsDialogComponent} from "./websites/website/hosting/manage-dns/add-dns-dialog/add-dns-dialog.component";
import {SplitBySpacePipe} from "./shared/pipes/split-by-space.pipe";
import {
  GrantAccessCopyWebsiteDialogComponent
} from "./websites/website/move-to-10web/grant-access-website-copy/grant-access-copy-website-dialog.component";
import {
  WebsiteInstallTrialFlowComponent
} from "./websites/add-website-dialog/website-install-tial-flow/website-install-trial-flow.component";
import {
  AddRedirectDialogComponent
} from "./websites/website/hosting/redirects/add-redirect-dialog/add-redirect-dialog.component";
import {TooltipShowAnimatedDirective} from './shared/components/tooltip-show-animated/tooltip-show-animated-directive';
import {TooltipShowAnimatedComponent} from "./shared/components/tooltip-show-animated/tooltip-show-animated.component";
import {WhiteLabelComponent} from "./account/white-label/white-label.component";
import {
  SubwebsiteInstallComponent
} from "./websites/add-website-dialog/subwebsite-install/subwebsite-install.component";
import {AdvancedToolsComponent} from "./websites/website/advanced-tools/advancedTools.component";
import {WhatsNewDialogComponent} from "./websites/whats-new-dialog/whats-new-dialog.component";
import {ScoreWidgetComponent} from "./websites/website/overview/score-widget/score-widget.component";
import {
  ScoreInfoTooltipHoverComponent
} from "./shared/components/score-info-tooltip-hover/score-info-tooltip-hover.component";
import {SafeHtmlPipe} from "./shared/pipes/safe-html";
import {TrustPilotComponent} from "./shared/components/trustpilot/trustpilot.component";
import {HostingEnvironmentComponent} from "./websites/website/hosting/environment/hosting-environment.component";
import {TourComponent} from "./shared/components/tour/tour.component";
import * as Sentry from '@sentry/browser';
import {
  RecreateWebsiteComponent
} from "./websites/website/builder-templates/recreate-website/recreate-website.component";
import {TemplatesComponent} from "./websites/website/builder-templates/templates/templates.component";
import {
  RecreateWebsiteStepsComponent
} from "./websites/website/builder-templates/recreate-website-steps/recreate-website-steps.component";
import {
  StartAiBuilderDialogComponent
} from "./websites/website/builder-templates/start-ai-builder-dialog/start-ai-builder-dialog.component";
import {
  AiBuilderNoticeDialogComponent
} from "./websites/website/builder-templates/ai-builder-notice-dialog/ai-builder-notice-dialog.component";
import {
  AiBuilderErrorDialogComponent
} from "./websites/website/builder-templates/ai-builder-error-dialog/ai-builder-error-dialog.component";

import {
  UpgradeOfferWidgetComponent
} from "./websites/website/overview/upgrade-offer-widget/upgrade-offer-widget.component";
import {
  SmallScreenDisclaimerDialogComponent
} from './shared/components/dialogs/small-screen-disclaimer/small-screen-disclaimer-dialog.component';
import {BuilderSignoutGuard} from "./auth/builder-signout.guard";
import {AiBuilderHeaderBannerComponent} from "./ai-builder-header-banner/ai-builder-header-banner.component";
import {BuilderSignoutComponent} from "./auth/builder-signout/builder-signout.component";
import {TwoFADialogComponent} from "./shared/components/dialogs/2fa-dialog/2fa-dialog.component";
import {DbFormatDatePipe} from "./shared/pipes/db-formatDate.pipe";
import {MobileDisclaimerComponent} from "./mobile-disclaimer/mobile-disclaimer.component";
import {
  AlreadyHaveAccountDialogComponent
} from "./shared/components/dialogs/already-have-account/already-have-account-dialog.component";
import {SSlFailedDialogComponent} from "./websites/website/hosting/tools/ssl-failed-dialog/ssl-failed-dialog.component";
import {
  ManualOptimizationDialogComponent
} from "./websites/manual-optimization-dialog/manual-optimization-dialog.component";
import {MobileAvailableComponent} from "./shared/components/dialogs/mobile-available/mobile-available.component";
import {WebsiteTitleComponent} from "./website-title-mobile/website-title.component";
import {OptimizerLoadingComponent} from "./optimizer-loading-dialog/optimizer-loading.component";
import {
  MigrationManualSummaryComponent
} from "./websites/add-website-dialog/migration-manual-summary/migration-manual-summary.component";
import {OnboardingComponent} from "./websites/website/onboarding/onboarding.component";
import {
  NotificationDialogComponent
} from './shared/components/dialogs/notification-dialog/notification-dialog.component';
import {LoadingWorkspaceComponent} from './shared/components/loading-workspace/loading-workspace.component';
import {LoadingLineComponent} from './shared/components/loading-line/loading-line.component';
import {WorkspacePopupComponent} from './workspace-popup/workspace-popup.component';
import {UserManagementComponent} from "./user-managment/user-management.component";
import {UserManagementService} from "./user-managment/user-management.service";
import {
  LeaveRemoveWorkspaceDialogComponent
} from './shared/components/dialogs/leave-remove-workspace-dialog/leave-remove-workspace-dialog.component';
import {
  ManageWorkspaceUsersDialogComponent
} from './shared/components/dialogs/manage-workspace-users-dialog/manage-workspace-users-dialog.component';
import {RechargeComponent} from "./shared/components/dialogs/recharge-dialog/recharge.component";
import {RechargeHeaderBannerComponent} from "./recharge-header-banner/recharge-header-banner.component";
import {AdditionalWebsiteComponent} from "./auth/checkout/additional-website/additional-website.component";
import {DomainsExceededComponent} from "./websites/add-website-dialog/domains-exceeded/domains-exceeded.component";
import {GoogleScoreComponent} from "./websites/add-website-dialog/google-score/google-score.component";
import {
  MoveTo10webConnectedComponent
} from './websites/website/move-to-10web/move-to10web-connected/move-to10web-connected.component';
import {
  MoveTo10webHostedComponent
} from './websites/website/move-to-10web/move-to10web-hosted/move-to10web-hosted.component';
import {HostingToolsResolver} from "./websites/website/hosting/tools/hosting-tools-resolver";
import {MigrationStepsComponent} from './shared/components/migration-steps/migration-steps.component';
import {
  WebsiteInstallLoadingDialogComponent
} from "./websites/add-website-dialog/website-install-loading-dialog/website-install-loading-dialog.component";
import {AddWebsiteResolver} from "./add-website/add-website.resolver";
import {
  MigrationSuccessDialogComponent
} from './websites/add-website-dialog/migration-success-dialog/migration-success-dialog.component';
import {RoundNumberPipe} from "./shared/pipes/round-number.pipe";
import {
  MigrationRequestSentDialogComponent
} from "./websites/add-website-dialog/migration-request-sent-dialog/migration-request-sent-dialog.component";
import {ZoomInDialogComponent} from './shared/components/dialogs/zoom-in-dialog/zoom-in-dialog.component';
import {
  SatisfactionSurveyWidgetComponent
} from "./websites/website/overview/satisfaction-survey-widget/satisfaction-survey-widget.component";
import {AddOwnWorkspaceComponent} from "./add-own-workspace/add-own-workspace.component";
import {WorkspaceComponent} from "./workspace/workspace.component";
import {WorkspaceSettingsComponent} from "./workspace/workspace-settings/workspace-settings.component";
import {WorkspaceService} from "./workspace/workspace.service";
import {BillingDetailsComponent} from "./workspace/billing-details/billing-details.component";
import {UserManagementResolver} from "./user-managment/user-management.resolver";
import {BillingInfoResolver} from "./workspace/billing-details/billing-info-resolver";
import {WorkspacePopupFirstTimeComponent} from "./workspace-popup__first-time/workspace-popup-first-time.component";
import {DbPlanLabelComponent} from "./workspace/subscription-plan/db-plan-label/db-plan-label.component";
import {DbPaymentButtonComponent} from "./workspace/subscription-plan/db-payment-button/db-payment-button.component";
import {DbBillingInfoComponent} from "./workspace/subscription-plan/db-billing-info/db-billing-info.component";
import {
  AgencyPartnerProgramDialogComponent
} from './add-website/agency-partner-program-dialog/agency-partner-program-dialog.component';
import {UserLocalStorageResolver} from "./user/user-local-storage.resolver";
import {LoadedDirective} from './shared/directives/loaded/loaded.directive';
import {MigrateOnlyComponent} from './add-website/migrate-only/migrate-only.component';
import {CreateMigrateComponent} from './add-website/create-migrate/create-migrate.component';
import {ConnectOnlyComponent} from './add-website/connect-only/connect-only.component';
import {DsTooltipDirective} from './shared/directives/ds-tooltip/ds-tooltip.directive';
import {DsProgressBarComponent} from './shared/components/ds-progress-bar/ds-progress-bar.component';
import {DsTabsComponent} from "./shared/components/ds-tabs/ds-tabs.component";
import {DsTabContentComponent} from "./shared/components/ds-tabs/tab-content/ds-tab-content.component";
import {
  AddWebsiteChoiceActivationFlowComponent
} from "./websites/add-website-dialog/add-website-choice-activation-flow/add-website-choice-activation-flow.component";
import {NotificationDialogService} from "./shared/components/dialogs/notification-dialog/notification-dialog.service";
import {MigrateOnlyDialogComponent} from "./websites/add-website-dialog/migrate-only/migrate-only.component";
import {OptimizationPreviewComponent} from './optimization-preview/optimization-preview.component';
import {UrlStepComponent} from './optimization-preview/url-step/url-step.component';
import {CompareStepComponent} from './optimization-preview/compare-step/compare-step.component';
import {OptimizeStepComponent} from './optimization-preview/optimize-step/optimize-step.component';
import {DsPaginationControlsComponent} from "./shared/components/ds-pagination/pagination-controls.component";
import {DsDropdownComponent} from './shared/components/ds-dropdown/ds-dropdown.component';
import {DsDropdownListComponent} from './shared/components/ds-dropdown/ds-dropdown-list/ds-dropdown-list.component';
import {DsMobilePageTitleComponent} from './shared/components/ds-mobile-page-title/ds-mobile-page-title.component';
import {
  AddOwnWorkspaceDialogComponent
} from "./shared/components/dialogs/add-own-workspace/add-own-workspace-dialog.component";
import {DsSearchInputComponent} from "./shared/components/ds-search-input/ds-search-input.component";
import {MigrationDialogComponent} from "./shared/components/dialogs/migration/migration-dialog.component";
import {
  TrialInvitedBannerComponent
} from "./websites/hosting-offer-banner/trial-invited-banner/trial-invited-banner.component";
import {
  HolidayOfferBannerComponent
} from './websites/hosting-offer-banner/holiday-offer-banner/holiday-offer-banner.component';
import {FirstTimeHighlightComponent} from "./shared/components/first-time-highlights/first-time-highlight.component";
import {FirstTimeHighlightDirective} from "./shared/components/first-time-highlights/first-time-highlight.directive";
import {WebsitesListComponent} from './shared/components/websites-list/websites-list.component';
import {DsBreadcrumbsComponent} from './shared/components/ds-breadcrumbs/ds-breadcrumbs.component';
import {FilterByIntersectPipe} from "./shared/pipes/filter-by-intersect.pipe";
import {SetupComponent} from './components/pages/setup/setup.component';
import {OtherStepComponent} from "./components/pages/setup/other/other.component";
import {ExternalApiService} from "./auth/externalApi.service";
import {ChangeHostingComponent} from "./components/pages/setup/change-hosting/change-hosting.component";
import {CreateComponent} from "./components/pages/setup/create/create.component";
import {CreateOptionsComponent} from "./components/pages/setup/create/options/create-options.component";
import {CarouselComponent} from './shared/components/carousel/carousel.component';
import {PaymentInfoComponent} from './shared/components/payment-info/payment-info.component';
import {OptimizeComponent} from "./components/pages/setup/optimize/optimize.component";
import {StartBoosterComponent} from "./components/pages/setup/start-booster/start-booster.component";
import {OptimizeWithMigrateComponent} from "./add-website/optimize-with-migrate/optimize-with-migrate.component";
import {DsScoreComponent} from './shared/components/ds-score/ds-score.component';
import {DsCircleComponent} from './shared/components/ds-circle/ds-circle.component';
import {
  PlatformAccountUpgradedComponent
} from './shared/components/dialogs/account-upgraded-dialog/platform-account-upgraded/platform-account-upgraded.component';
import {
  EcommerceCompleteSetupComponent
} from './shared/components/dialogs/account-upgraded-dialog/ecommerce-complete-setup/ecommerce-complete-setup.component';
import {SlackCommunityComponent} from './shared/components/slack-community/slack-community.component';
import {
  InformativeBannerFeatureComponent
} from "./shared/components/informative-banner-feature/informative-banner-feature.component";
import {
  AiBuilderDemoBannerComponent
} from "./shared/components/ai-builder-demo-banner/ai-builder-demo-banner.component";
import {EnvSwitcherComponent} from "./websites/website-item-menu/env-switcher/env-switcher.component";
import {QuestionStepComponent} from "./components/pages/setup/question-step/question-step.component";
import {MoveHostingComponent} from "./components/pages/setup/move-hosting/move-hosting.component";
import {RecreateAiComponent} from "./components/pages/setup/recreate-ai/recreate-ai.component";
import {DbActiveModal, DbModalModule} from "./modules/modal/modal.module";
import {
  WebsiteAvailableDialogComponent
} from "./shared/components/dialogs/website-available-dialog/website-available-dialog.component";
import {MigrateWebsiteLampComponent} from "./websites/migrate-website-lamp/migrate-website-lamp.component";
import {CreateWebsiteComponent} from './components/common/create-website/create-website.component';
import {CloneWebsiteComponent} from './components/common/clone-website/clone-website.component';
import {
  CloneInstallationComponent
} from './components/common/clone-website/clone-installation/clone-installation.component';
import {ActivityLogFiltersComponent} from "./account/activity-log/filters/filters.component";
import {ActivityLogComponent} from "./account/activity-log/activity-log.component";
import {ActivitiesListComponent} from './account/activity-log/activities-list/activities-list.component';
import {ActivityLogResolver} from "./account/activity-log/activity-log.resolver";
import {DbDatepickerModule} from "./modules/datepicker/datepicker.module";
import {ActivitiesTextPipe} from './account/activity-log/activities-text.pipe';
import {ActivityLogDeletedDomainsResolver} from "./account/activity-log/activity-log-deleted-domains.resolver";
import {ActivityLogDeletedClientsResolver} from "./account/activity-log/activity-log-deleted-clients.resolver";
import {SomethingWentWrongComponent} from "./something-went-wrong/something-went-wrong.component";
import {BoosterComponent} from "./websites/website/booster/booster.component";
import {FrontEndComponent} from "./websites/website/booster/front-end/front-end.component";
import {OptimizeImagesComponent} from "./websites/website/booster/front-end/optimize-images/optimize-images.component";
import {
  OptimizeImagesComponentFree
} from "./websites/website/booster/front-end/optimize-images-free/optimize-images-free.component";
import {versions} from '../environments/versions';
import {WebsiteItemOverlayComponent} from './websites/website-item/website-item-overlay/website-item-overlay.component';
import {WebsiteItemActionsComponent} from './websites/website-item/website-item-actions/website-item-actions.component';
import {TagItemComponent} from "./websites/tag-managment-dialogs/tag-item/tag-item.component";
import {
  OneProdBundleOfferBannerComponent
} from './websites/hosting-offer-banner/one-prod-bundle-offer-banner/one-prod-bundle-offer-banner.component';
import {FilterPipe} from './shared/pipes/filter.pipe';
import {SettingsComponent} from "./websites/website/booster/settings/settings.component";
import {CustomRulesComponent} from "./websites/website/booster/front-end/custom-rules/custom-rules.component";
import {BoosterGeneralComponent} from './websites/website/booster/front-end/booster-general/booster-general.component';
import {
  BoosterOptimizationStatusComponent
} from './websites/website/booster/front-end/booster-general/booster-optimization-status/booster-optimization-status.component';
import {
  NewVersionAvailableComponent
} from './websites/website/booster/front-end/booster-general/new-version-available/new-version-availble.component';
import {
  BoosterPageScoreComponent
} from './websites/website/booster/front-end/booster-general/booster-page-score/booster-page-score.component';
import {
  BoosterPageOptimizerComponent
} from './websites/website/booster/front-end/booster-general/booster-page-optimizer/booster-page-optimizer.component';
import {
  BoosterFeaturesListComponent
} from './websites/website/booster/front-end/booster-general/booster-features-list/booster-features-list.component';
import {
  OptimizationDialogComponent
} from './websites/website/booster/dialogs/optimization-dialog/optimization-dialog.component';
import {
  OptimizationProgressComponent
} from './websites/website/booster/dialogs/optimization-dialog/optimization-progress/optimization-progress.component';
import {
  ApiUnavailableComponent
} from './websites/website/booster/dialogs/optimization-dialog/api-unavailable/api-unavailable.component';
import {
  PlanNotSupportedComponent
} from './websites/website/booster/dialogs/optimization-dialog/plan-not-supported/plan-not-supported.component';
import {
  OptimizationSuccessComponent
} from './websites/website/booster/dialogs/optimization-dialog/optimization-success/optimization-success.component';
import {
  WebPViaServerDialogComponent
} from "./websites/website/booster/dialogs/webp-via-server-dialog/webp-via-server-dialog.component";
import {
  RemoveBoostedPageDialogComponent
} from './websites/website/booster/dialogs/remove-boosted-page-dialog/remove-boosted-page-dialog.component';
import {
  BoosterFeaturesListPaidComponent
} from './websites/website/booster/front-end/booster-general/booster-features-list-paid/booster-features-list-paid.component';
import {BackEndComponent} from './websites/website/booster/back-end/back-end.component';
import {BenchmarkComponent} from './websites/website/booster/back-end/benchmark/benchmark.component';
import {
  BenchmarkTableComponent
} from './websites/website/booster/back-end/benchmark/benchmark-table/benchmark-table.component';
import {DsAccordionComponent} from './shared/components/ds-accordion/ds-accordion.component';
import {
  BackEndFeaturesComponent
} from './websites/website/booster/back-end/back-end-features/back-end-features.component';
import {
  BoosterPluginDownloadDialogComponent
} from './websites/website/booster/dialogs/booster-plugin-download-dialog/booster-plugin-download-dialog.component';
import {ModesListComponent} from "./websites/website/booster/front-end/custom-rules/modes-list/modes-list.component";
import {BenchmarkResolver} from "./websites/website/booster/back-end/benchmark.resolver";
import {
  BenchmarkResultComponent
} from './websites/website/booster/back-end/benchmark/benchmark-table/benchmark-result/benchmark-result.component';
import {
  BoosterFeatureStatusComponent
} from './websites/website/booster/front-end/booster-general/booster-feature-status/booster-feature-status.component';
import {
  BoosterTroubleInstructionsComponent
} from './websites/website/booster/dialogs/booster-trouble-instructions/booster-trouble-instructions.component';
import {
  BoosterDisconnectedComponent
} from './websites/website/booster/booster-disconnected/booster-disconnected.component';
import {
  CreateAiStepsComponent
} from './components/pages/setup/create-ai/create-ai-builder/components/create-ai-steps/create-ai-steps.component';
import {
  CreateAiProcessComponent
} from './components/pages/setup/create-ai/create-ai-builder/components/create-ai-process/create-ai-process.component';
import {AiBuilderComponent} from './websites/website/ai-builder/ai-builder.component';
import {
  AiBuilderDisabledComponent
} from './websites/website/ai-builder/ai-builder-disabled/ai-builder-disabled.component';
import {AiBuilderPagesComponent} from './websites/website/ai-builder/ai-builder-pages/ai-builder-pages.component';
import {
  AiBuilderPagesTableComponent
} from './websites/website/ai-builder/ai-builder-pages/ai-builder-pages-table/ai-builder-pages-table.component';
import {
  AiBuilderCreatePageComponent
} from './websites/website/ai-builder/dialogs/ai-builder-create-page/ai-builder-create-page.component';
import {
  AiBuilderCreatePageOptionsComponent
} from './websites/website/ai-builder/dialogs/ai-builder-create-page/ai-builder-create-page-options/ai-builder-create-page-options.component';
import {
  AiBuilderCreatePageBuilderComponent
} from './websites/website/ai-builder/dialogs/ai-builder-create-page/ai-builder-create-page-builder/ai-builder-create-page-builder.component';
import {
  AiBuilderCreatePageBlankComponent
} from './websites/website/ai-builder/dialogs/ai-builder-create-page/ai-builder-create-page-blank/ai-builder-create-page-blank.component';
import {
  AiBuilderCreatePageTemplatesComponent
} from './websites/website/ai-builder/dialogs/ai-builder-create-page/ai-builder-create-page-templates/ai-builder-create-page-templates.component';
import {
  AiBuilderCreatePagePreviewDemoComponent
} from "./websites/website/ai-builder/dialogs/ai-builder-create-page/ai-builder-create-page-preview-demo/ai-builder-create-page-preview-demo.component";
import {AdvancedSettingsComponent} from "./websites/website/booster/advanced/advanced-settings.component";
import {
  SubscriptionBlockWithPagesComponent
} from "./websites/website/booster/subscription-block-with-pages/subscription-block-with-pages.component";
import {
  OptimizeWithBoosterProComponent
} from "./websites/website/booster/optimize-with-booster-pro/optimize-with-booster-pro.component";
import {TrimHomeUrlPipe} from "./shared/pipes/trimHomeUrl.pipe";
import {
  AiBuilderPageItemComponent
} from "./websites/website/ai-builder/ai-builder-pages/ai-builder-page-item/ai-builder-page-item.component";
import {OneMonthFreeComponent} from './shared/components/dialogs/one-month-free/one-month-free.component';
import {
  OneMonthFreeFeaturesComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-features/one-month-free-features.component';
import {
  OneMonthFreeHireExpertComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-hire-expert/one-month-free-hire-expert.component';
import {
  OneMonthFreePlansComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-plans/one-month-free-plans.component';
import {
  OneMonthFreeSelectPlanComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-select-plan/one-month-free-select-plan.component';
import {
  OneMonthFreeCheckoutComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-checkout/one-month-free-checkout.component';
import {
  OneMonthFreeEditorBannerComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-editor-banner/one-month-free-editor-banner.component';
import {
  OneMonthFreeCheckoutInfoComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-checkout-info/one-month-free-checkout-info.component';
import {
  OneMonthFreePaymentStepsComponent
} from './shared/components/dialogs/one-month-free/components/one-month-free-payment-steps/one-month-free-payment-steps.component';
import {
  PaidFeatureUpgradeComponent
} from './shared/components/dialogs/paid-feature-upgrade/paid-feature-upgrade.component';
import {
  PaidFeatureUpgradeCheckoutComponent
} from './shared/components/dialogs/paid-feature-upgrade/components/paid-feature-upgrade-checkout/paid-feature-upgrade-checkout.component';
import {
  PaidFeatureUpgradeBannerComponent
} from './shared/components/dialogs/paid-feature-upgrade/components/paid-feature-upgrade-banner/paid-feature-upgrade-banner.component';
import {WebsitesGuard} from "./websites/websites.guard";

import {
  TestModeConfirmationComponent
} from "./websites/website/booster/dialogs/test-mode-confiramtion/test-mode-confirmation.component";
import {
  BoosterUpdateComponent
} from "./websites/website/booster/dialogs/booster-update-dialog/booster-update.component";
import {CountdownPipe} from './shared/pipes/countdown.pipe';
import {WebsitesControlsComponent} from './websites/websites-controls/websites-controls.component';
import {
  WebsitesControlsCardComponent
} from './websites/websites-controls/websites-controls-card/websites-controls-card.component';
import {
  BoosterFeaturesListMobileComponent
} from "./websites/website/booster/front-end/booster-general/booster-features-list/booster-features-list-mobile/booster-features-list-mobile.component";
import {
  OptimizationLevelsComponent
} from "./websites/website/booster/front-end/booster-general/optimization-levels/optimization-levels.component";
import {AnniversaryOfferComponent} from "./components/common/anniversary-offer/anniversary-offer.component";
import {
  AnniversaryOfferBannerComponent
} from "./components/common/anniversary-offer/components/anniversary-offer-banner/anniversary-offer-banner.component";
import {
  CreateWebsiteOptionsComponent
} from './components/common/create-website/components/create-website-options/create-website-options.component';
import {
  CreateWebsiteInstallComponent
} from './components/common/create-website/components/create-website-install/create-website-install.component';
import {
  CreateWebsiteInstallCloneComponent
} from './components/common/create-website/components/create-website-install-clone/create-website-install-clone.component';
import {
  CreateWebsiteInstallBlankComponent
} from './components/common/create-website/components/create-website-install-blank/create-website-install-blank.component';
import {
  CreateWebsiteInstallFooterComponent
} from './components/common/create-website/components/create-website-install-footer/create-website-install-footer.component';
import {
  CreateWebsiteInstallWebsitesListComponent
} from './components/common/create-website/components/create-website-install-websites-list/create-website-install-websites-list.component';
import {
  DownloadManagerRecreateComponent
} from './components/common/download-manager-recreate/download-manager-recreate.component';
import {
  DownloadManagerConnectComponent
} from './components/common/download-manager-connect/download-manager-connect.component';
import {LimitExceededComponent} from './components/common/limit-exceeded/limit-exceeded.component';
import {MigrateWebsiteComponent} from './components/common/migrate-website/migrate-website.component';
import {
  MigrateWebsiteFooterComponent
} from './components/common/migrate-website/components/migrate-website-footer/migrate-website-footer.component';
import {
  ManualMigrateWebsiteComponent
} from './components/common/manual-migrate-website/manual-migrate-website.component';
import {
  ManualMigrateWebsiteSubdomainComponent
} from './components/common/manual-migrate-website/components/manual-migrate-website-subdomain/manual-migrate-website-subdomain.component';
import {
  ManualMigrateWebsiteHostingCredentialsComponent
} from './components/common/manual-migrate-website/components/manual-migrate-website-hosting-credentials/manual-migrate-website-hosting-credentials.component';
import {
  ManualMigrateWebsiteDetailsComponent
} from './components/common/manual-migrate-website/components/manual-migrate-website-details/manual-migrate-website-details.component';
import {
  ManualMigrateWebsiteSummaryComponent
} from './components/common/manual-migrate-website/components/manual-migrate-website-summary/manual-migrate-website-summary.component';
import {
  ManualMigrateWebsiteSummaryDetailsComponent
} from './components/common/manual-migrate-website/components/manual-migrate-website-summary-details/manual-migrate-website-summary-details.component';
import {
  ManualMigrateWebsiteRequestSentComponent
} from './components/common/manual-migrate-website/components/manual-migrate-website-request-sent/manual-migrate-website-request-sent.component';
import {CreateSubSiteComponent} from './components/common/create-subsite/create-sub-site.component';
import {
  CreateWebsiteSuccessComponent
} from './components/common/create-website-success/create-website-success.component';
import {
  CreateWebsiteSuccessScoreComponent
} from './components/common/create-website-success/components/create-website-success-score/create-website-success-score.component';
import {
  WebsitesControlsDetailedComponent
} from './websites/websites-controls-detailed/websites-controls-detailed.component';
import {
  WebsitesControlsDetailedCardComponent
} from './websites/websites-controls-detailed/components/websites-controls-detailed-card/websites-controls-detailed-card.component';
import {UpgradeTenWebProComponent} from './components/common/upgrade-ten-web-pro/upgrade-ten-web-pro.component';
import {CreateWebsiteTrialComponent} from './components/common/create-website-trial/create-website-trial.component';
import {
  CreateWebsiteTrialInstallComponent
} from './components/common/create-website-trial/components/create-website-trial-install/create-website-trial-install.component';
import {CDNComponent} from "./websites/website/booster/cdn/cdn.component";
import {
  BoosterImageOptimizerComponent
} from './websites/website/booster/booster-image-optimizer/booster-image-optimizer.component';
import {
  NotificationNoticesComponent
} from "./websites/website/booster/front-end/notification-notices/notification-notices.component";
import {
  PaidFeatureUpgradeFeaturesComponent
} from './shared/components/dialogs/paid-feature-upgrade/components/paid-feature-upgrade-features/paid-feature-upgrade-features.component';
import {
  PaidFeatureUpgradePlansComponent
} from './shared/components/dialogs/paid-feature-upgrade/components/paid-feature-upgrade-plans/paid-feature-upgrade-plans.component';
import {CdnDisabledComponent} from "./websites/website/booster/cdn/cdn-disabled/cdn-disabled.component";
import {CdnOverviewComponent} from './websites/website/booster/cdn/components/cdn-overview/cdn-overview.component';
import {
  CdnFeatureStackComponent
} from './websites/website/booster/cdn/components/cdn-feature-stack/cdn-feature-stack.component';
import {
  CdnClearCacheComponent
} from './websites/website/booster/cdn/components/cdn-clear-cache/cdn-clear-cache.component';
import {CdnGeneralComponent} from './websites/website/booster/cdn/components/cdn-general/cdn-general.component';
import {RemoveWebsiteComponent} from './websites/remove-website/remove-website.component';
import {
  CdnFullPageCacheComponent
} from "./websites/website/booster/cdn/components/cdn-full-page-cache/cdn-full-page-cache.component";
import {
  CdnFullPageCacheToggleComponent
} from "./websites/website/booster/cdn/components/cdn-full-page-cache/toggle/cdn-full-page-cache-toggle.component";
import {
  CdnFullPageCacheSettingsComponent
} from "./websites/website/booster/cdn/components/cdn-full-page-cache/settings/cdn-full-page-cache-settings.component";
import {CdnSettingsComponent} from "./websites/website/booster/cdn/components/cdn-settings/cdn-settings.component";
import {
  CdnDisableEnterpriseComponent
} from "./websites/website/booster/cdn/components/cdn-settings/cdn-disable-enterprise/cdn-disable-enterprise.component";
import {
  CdnOriginalServerComponent
} from "./websites/website/booster/cdn/components/cdn-settings/cdn-original-server/cdn-original-server.component";
import {
  EnableCloudflareDialogComponent
} from "./websites/website/booster/cdn/dialogs/enable-cloudflare-integration/enable-cloudflare-dialog.component";
import {
  VerifyHostnameComponent
} from "./websites/website/booster/cdn/dialogs/verify-cloudflare-hostname/verify-hostname.component";
import {
  DisableCloudflareEnterpriseComponent
} from "./websites/website/booster/cdn/dialogs/disable-cloudflare-enterprise/disable-cloudflare-enterprise.component";
import {
  UpdateBoosterPluginComponent
} from "./websites/website/booster/dialogs/update-booster-plugin/update-plugin.component";
import {
  BoosterProUpgradedComponent
} from "./shared/components/dialogs/account-upgraded-dialog/booster-account-upgraded/booster-account-upgraded.component";
import {NotifyDialogComponent} from "./websites/website/booster/dialogs/notify-dialog/notify-dialog.component";
import {EnableCdnComponent} from "./websites/website/booster/front-end/booster-general/enable-cdn/enable-cdn.component";
import {DbNumberFormatPipe} from "./shared/pipes/db-number-ceil.pipe";
import {InformativeDialogComponent} from "./components/common/informative-dialog/informative-dialog.component";
import {PercentOfferComponent} from './components/common/percent-offer/percent-offer.component';
import {
  PercentOfferBannerComponent
} from './components/common/percent-offer/components/percent-offer-banner/percent-offer-banner.component';
import {
  PercentOfferCounterComponent
} from './components/common/percent-offer/components/percent-offer-counter/percent-offer-counter.component';
import {CdnPendingComponent} from "./websites/website/booster/cdn/cdn-pending/cdn-pending.component";
import {
  EditOriginalIpComponent
} from "./websites/website/booster/cdn/dialogs/edit-original-ip/edit-original-ip.component";
import {BoosterTroubleshootResolver} from "./websites/website/booster/boosterTroubleshootResolver";
import {DbCdnInfoComponent} from "./workspace/subscription-plan/db-cdn-info/db-cdn-info.component";
import {
  CdnEnableBannerComponent
} from "./websites/website/booster/front-end/cdn-enable-banner/cdn-enable-banner.component";
import {
  ConnectAndOptimizeComponent
} from './components/pages/setup/connect-and-optimize/connect-and-optimize.component';
import {DsFilterChipsComponent} from './shared/components/ds-filter-chips/ds-filter-chips.component';
import {
  MigrateForBoosterComponent
} from "./websites/website/booster/back-end/migrate-for-booster/migrate-for-booster.component";
import {DbDecimalPipe} from "./shared/pipes/decimal.pipe";
import {
  DbPlanLabelBoosterComponent
} from "./workspace/subscription-plan/db-plan-booster/db-plan-label-booster.component";
import {DbNegativeNumberToZeoPipe} from "./shared/pipes/negative-number-to-zeo.pipe";
import {
  AdditionalHostingAddonComponent
} from "./auth/checkout/additional-hosting-addon/additional-hosting-addon.component";
import {BoosterProComponent} from "./workspace/upgrade-plan/booster-pro/booster-pro.component";
import {CdnTestComponent} from "./websites/website/booster/cdn/cdn-test/cdn-test.component";
import {
  CreateAiAssistantComponent
} from './components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant.component';
import {
  CreateAiBuilderComponent
} from './components/pages/setup/create-ai/create-ai-builder/create-ai-builder.component';
import {
  CreateAiAssistantFormViewComponent
} from './components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-form-view/create-ai-assistant-form-view.component';
import {
  CreateAiAssistantStartViewComponent
} from './components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-start-view/create-ai-assistant-start-view.component';
import {
  CreateAiAssistantFinalizeComponent
} from './components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-finalize/create-ai-assistant-finalize.component';
import {
  RecreateFirstTimeComponent
} from './websites/website/ai-builder/recreate-first-time/recreate-first-time.component';
import {NotificationModalComponent} from './shared/components/dialogs/notification-modal/notification-modal.component';
import {
  AdditionalHostingAddonInfoComponent
} from "./auth/checkout/additional-hosting-addon-info/additional-hosting-addon-info.component";
import {BoosterProTempComponent} from "./websites/website/booster/booster-pro/booster-pro-temp.component";
import {
  CreateAiAssistantTemplateSelectComponent
} from './components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-template-select/create-ai-assistant-template-select.component';
import {
  OneMonthFreeBuilderComponent
} from "./shared/components/dialogs/one-month-free/components/one-month-free-builder/one-month-free-builder.component";
import {
  OneWeekFreeBuilderMobileComponent
} from "./shared/components/dialogs/one-month-free/components/one-week-free-builder-mobile/one-week-free-builder-mobile.component";
import {
  OptimizationWithHostingComponent
} from "./websites/website/booster/back-end/optimization-with-hosting-booster/optimization-with-hosting.component";
import {
  DemoProgressComponent
} from "./websites/website/booster/front-end/booster-general/dialogs/demo-cdn-progress/demo-progress.component";
import {
  UpgradeBoosterProComponent
} from "./shared/components/dialogs/paid-feature-upgrade/components/pro-booster-upgrade-features/pro-booster-features.component";
import {
  BoosterProSelectPlanComponent
} from "./shared/components/dialogs/paid-feature-upgrade/components/booster-pro-select-plan/booster-pro-select-plan.component";
import {
  GenerationFailedComponent
} from './components/pages/setup/create-ai/create-ai-assistant/generation-failed/generation-failed.component';
import {DemoRequestsInterceptor} from "./shared/interceptors/demo-requests.interceptor";
import {
  CreateAiAssistantCompanyTypeComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-company-type/create-ai-assistant-company-type.component";
import {
  CreateAiAssistantTemplateComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-template-select/create-ai-assistant-template/create-ai-assistant-template.component";
import {
  CdnNoticeSectionComponent
} from "./websites/website/booster/cdn/components/cdn-settings/cdn-notif/cdn-notice-section.component";
import {CdnNoticeComponent} from "./websites/website/booster/cdn-notiification/cdn-notice.component";
import {
  ImageOptimizationModesComponent
} from "./websites/website/booster/image-optimization-modes/image-optimization-modes.component";
import {
  ImageOptimizerSectionComponent
} from "./websites/website/booster/front-end/image-optimizer/image-optimizer-section.component";
import {
  ImageMediaLibraryComponent
} from "./websites/website/booster/front-end/image-optimizer/image-media-library/image-media-library.component";
import {
  ImageOptimizerPagesComponent
} from "./websites/website/booster/front-end/image-optimizer/image-optimizer-pages/image-optimizer-pages.component";
import {
  ImageOptimizerWebpComponent
} from "./websites/website/booster/front-end/image-optimizer-webp/image-optimizer-webp.component";
import {
  ImageLimitsBannerComponent
} from "./websites/website/booster/front-end/image-optimizer/image-limits-banner/image-limits-banner.component";
import {
  BoosterActivationBannerComponent
} from "./shared/components/booster-activation-banner/booster-activation-banner.component";
import {
  ImageOptimizerSuccessComponent
} from './websites/website/booster/image-optimizer-success/image-optimizer-success.component';
import {
  BoosterFreePlanIntroComponent
} from './shared/components/dialogs/booster-free-plan-intro/booster-free-plan-intro.component';
import {
  OptimizedWebsitesCardComponent
} from './auth/sign-up/components/optimized-websites-card/optimized-websites-card.component';
import {
  OneMonthFreeDemoComponent
} from "./shared/components/dialogs/one-month-free/components/one-month-free-demo/one-month-free-demo.component";
import {
  SuccessAfterDemoUpgradeComponent
} from "./shared/components/dialogs/success-after-demo-upgrade/success-after-demo-upgrade.component";
import {RemoveDemoComponent} from "./shared/components/dialogs/remove-demo/remove-demo.component";
import {
  ApplyProUpgradeDialogComponent
} from "./websites/website/booster/front-end/booster-general/dialogs/apply-pro-upgrade-dialog/apply-pro-upgrade-dialog.component";
import {
  StartAiWebsiteBuildingComponent
} from "./components/pages/start-ai-website-building/start-ai-website-building.component";
import {
  CreateAiAssistantSignUpComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-sign-up/create-ai-assistant-sign-up.component";
import {
  LiveChatAvailableComponent
} from "./shared/components/dialogs/live-chat-available/live-chat-available.component";
import {
  AlternativeSubscriptionPlanComponent
} from './workspace/subscription-plan/alernative-subscription-plan/alternative-subscription-plan.component';
import {
  DsPaymentButtonComponent
} from './workspace/subscription-plan/alernative-subscription-plan/ds-payment-button/ds-payment-button.component';
import {
  DsCdnInfoComponent
} from './workspace/subscription-plan/alernative-subscription-plan/ds-cdn-info/ds-cdn-info.component';
import {
  DsBillingInfoComponent
} from './workspace/subscription-plan/alernative-subscription-plan/ds-billing-info/ds-billing-info.component';
import {
  CreateAiAssistantThemesComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-themes/create-ai-assistant-themes.component";
import {LowerCaseSpecificWordsPipe} from "./shared/pipes/lowerCaseSpecificWords.pipe";
import {DsMultiselectComponent} from "./shared/components/ds-multiselect/ds-multiselect.component";
import {BoosterWhiteLabelComponent} from "./websites/website/booster/white-label/white-label.component";
import {
  BoosterAdvancedSettingsComponent
} from "./websites/website/booster/front-end/advanced-settings/advanced-settings.component";
import {SiteLockIsOnComponent} from "./website-sidebar/website-lock-on/website-lock-on.component";
import {DeleteAccountDialogComponent} from "./account/security/delete-account-dialog/delete-account-dialog.component";
import {AccountService} from "./account/account.service";
import {AccountDeletedComponent} from "./account-deleted/account-deleted.component";
// Ecommerce
import {EcommerceComponent} from "./websites/website/ecommerce/ecommerce.component";
import {ProductsComponent} from "./websites/website/ecommerce/products/products.component";
import {ProductsTableComponent} from "./websites/website/ecommerce/products/products-table/products-table.component";
import {
  ProductTableRowComponent
} from "./websites/website/ecommerce/products/product-table-row/product-table-row.component";
import {EcommerceService} from "./websites/website/ecommerce/ecommerce.service";
import {AddProductComponent} from "./websites/website/ecommerce/products/add-product/add-product.component";
import {
  ProductActionsHeaderComponent
} from "./websites/website/ecommerce/products/add-product/product-actions-header/product-actions-header.component";
import {
  ProductDescriptionComponent
} from "./websites/website/ecommerce/products/add-product/product-description/product-description.component";
import {
  ProductMediaComponent
} from "./websites/website/ecommerce/products/add-product/product-media/product-media.component";
import {
  ProductMainPricingComponent
} from "./websites/website/ecommerce/products/add-product/product-main-pricing/product-main-pricing.component";
import {
  ProductPriceVariantsComponent
} from "./websites/website/ecommerce/products/add-product/product-price-variants/product-price-variants.component";
import {
  ProductInventoryComponent
} from "./websites/website/ecommerce/products/add-product/product-inventory/product-inventory.component";
import {
  ProductLinkedProductsComponent
} from "./websites/website/ecommerce/products/add-product/product-linked-products/product-linked-products.component";
import {
  ProductShippingComponent
} from "./websites/website/ecommerce/products/add-product/product-shipping/product-shipping.component";
import {
  ProductCategoryComponent
} from "./websites/website/ecommerce/products/add-product/product-category/product-category.component";
import {DsChipInputComponent} from "./shared/components/ds-chip-input/ds-chip-input.component";
import {FormControlPipe} from "./shared/pipes/as-form-control.pipe";
import {
  ProductVariantsDialogComponent
} from "./websites/website/ecommerce/products/add-product/product-variants-dialog/product-variants-dialog.component";
import {ProductAddHelperService} from "./websites/website/ecommerce/products/add-product/product-add-helper.service";
import {
  RemoveItemDialogComponent
} from "./websites/website/ecommerce/products/remove-item-dialog/remove-item-dialog.component";
import {CurrencyMaskDirective} from "./shared/directives/currency-mask/currency-mask.directive";
import {
  GenerateWithAiComponent
} from "./websites/website/ecommerce/products/add-product/generate-with-ai/generate-with-ai.component";
import {OrdersComponent} from "./websites/website/ecommerce/orders/orders.component";
import {OrdersTableComponent} from "./websites/website/ecommerce/orders/orders-table/orders-table.component";
import {OrderTableRowComponent} from "./websites/website/ecommerce/orders/order-table-row/order-table-row.component";
import {OrdersService} from "./websites/website/ecommerce/orders/orders.service";
import {OrderViewComponent} from "./websites/website/ecommerce/orders/order-view/order-view.component";
import {
  EcommerceViewBodyComponent
} from "./websites/website/ecommerce/ecommerce-item-view/ecommerce-view-body/ecommerce-view-body.component";
import {
  EcommerceViewHeaderComponent
} from "./websites/website/ecommerce/ecommerce-item-view/ecommerce-view-header/ecommerce-view-header.component";
import {
  EcommerceViewSidebarComponent
} from "./websites/website/ecommerce/ecommerce-item-view/ecommerce-view-sidebar/ecommerce-view-sidebar.component";
import {
  EcommerceViewStatsComponent
} from "./websites/website/ecommerce/ecommerce-item-view/ecommerce-view-stats/ecommerce-view-stats.component";
import {
  EcommerceItemViewComponent
} from "./websites/website/ecommerce/ecommerce-item-view/ecommerce-item-view.component";
import {CustomersComponent} from "./websites/website/ecommerce/customers/customers.component";
import {
  CustomersTableComponent
} from "./websites/website/ecommerce/customers/customers-table/customers-table.component";
import {
  CustomersTableRowComponent
} from "./websites/website/ecommerce/customers/customers-table-row/customers-table-row.component";
import {CustomersService} from "./websites/website/ecommerce/customers/customers.service";
import {CustomerViewComponent} from "./websites/website/ecommerce/customers/customer-view/customer-view.component";
import {ProductsListComponent} from "./websites/website/ecommerce/products/products-list/products-list.component";
import {OrdersListComponent} from "./websites/website/ecommerce/orders/orders-list/orders-list.component";
import {CustomersListComponent} from "./websites/website/ecommerce/customers/customers-list/customers-list.component";
import {
  DateRangePickerComponent
} from "./websites/website/ecommerce/mat-calendar/date-range-picker/date-range-picker.component";
import {CustomHeaderComponent} from "./websites/website/ecommerce/mat-calendar/custom-header/custom-header.component";
import {
  CustomRangePanelComponent
} from "./websites/website/ecommerce/mat-calendar/custom-header/custom-range-panel/custom-range-panel.component";
import {
  DsPlanInfoComponent
} from './workspace/subscription-plan/alernative-subscription-plan/ds-plan-info/ds-plan-info.component';
import {
  OneWeekFreeDemoComponent
} from "./shared/components/dialogs/one-month-free/components/one-week-free-demo/one-week-free-demo.component";
import {
  OneWeekFreeDemoOptionComponent
} from "./shared/components/dialogs/one-month-free/components/one-week-free-demo/one-week-free-demo__option/one-week-free-demo-option.component";
import {DbReplaceTextPipe} from "./shared/pipes/db-replace-text.pipe";
import {EcommerceDisabledComponent} from "./websites/website/ecommerce/ecommerce-disabled/ecommerce-disabled.component";
import {
  CreateAiAssistantChooseWebsiteTypeComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-choose-website-type/create-ai-assistant-choose-website-type.component";
import {
  CreateAiAssistantStoreTypeComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/ecommerce/create-ai-assistant-store-type/create-ai-assistant-store-type.component";
import {MainSidebarEmptyComponent} from "./main-sidebar-empty/main-sidebar-empty.component";
import {WoocommercePluginResolver} from "./websites/website/woocommerce-plugin-resolver";
import {NumberOnlyDirective} from "./shared/directives/number-only/number-only.directive";
import {
  WebsitesEmptyDedicatedHostingComponent
} from "./websites/websites-empty-dedicated-hosting/websites-empty-dedicated-hosting.component";
import {SwitchPlanComponent} from "./components/common/switch-plan/switch-plan.component";
import {
  SwitchAnnualDialogComponent
} from "./workspace/subscription-plan/alernative-subscription-plan/ds-billing-info/switch-annual-dialog/switch-annual-dialog.component";
import {
  SwitchPlanCheckoutComponent
} from "./components/common/switch-plan/switch-plan-checkout/switch-plan-checkout.component";
import {
  SwitchPlanAnnualComponent
} from "./components/common/switch-plan/switch-plan-annual/switch-plan-annual.component";
import {
  SignUpWithHubspotComponent
} from "./components/pages/setup/create-ai/create-ai-assistant/sign-up-with-hubspot/sign-up-with-hubspot.component";
import {QuillEditorComponent} from "./shared/components/quill-editor/quill-editor.component";
import {CheckoutFormComponent} from "./auth/checkout/checkout-form/checkout-form.component";
import {CheckoutChangePlanComponent} from "./auth/checkout/checkout-change-plan/checkout-change-plan.component";
import {CheckoutPlanInfoComponent} from "./auth/checkout/checkout-plan-info/checkout-plan-info.component";
import {PasswordValidationComponent} from "./components/common/password-validation/password-validation.component";
import {
  CheckoutPlanFeaturesComponent
} from "./auth/checkout/checkout-plan-info/checkout-plan-features/checkout-plan-features.component";
import {
  ChooseBundlePluginComponent
} from "./auth/checkout/checkout-plan-info/choose-bundle-plugin/choose-bundle-plugin.component";
import {ApplyCouponCodeComponent} from "./auth/checkout/apply-coupon-code/apply-coupon-code.component";
import {CheckoutPaymentInfoComponent} from "./auth/checkout/checkout-payment-info/checkout-payment-info.component";
import {
  MigrationRestApiBlockedComponent
} from "./shared/components/dialogs/migration-rest-api-blocked/migration-rest-api-blocked.component";
import {
  ManualMigrationDialogComponent
} from "./shared/components/dialogs/migration/manual-migration-dialog/manual-migration-dialog.component";
import {
  MigrationZipUploadComponent
} from "./shared/components/dialogs/migration/manual-migration-dialog/components/migration-zip-upload/migration-zip-upload.component";
import {
  MigrationProgressDialogComponent
} from "./shared/components/dialogs/migration/manual-migration-dialog/components/migration-progress-dialog/migration-progress-dialog.component";
import {EcommerceSettingsComponent} from "./websites/website/ecommerce/ecommerce-settings/ecommerce-settings.component";
import {EcommerceSettingsService} from "./websites/website/ecommerce/ecommerce-settings/ecommerce-settings.service";
import {
  ProfessionalPlanCardsComponent
} from "./auth/checkout/professional-plan-cards/professional-plan-cards.component";
import {ProfessionalPlanInfoComponent} from "./auth/checkout/professional-plan-info/professional-plan-info.component";
import {AiBuilderCommonService} from "./websites/website/ai-builder/ai-builder-common.service";
import {
  EcommerceMenuSidebarItemsComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/components/ecommerce-menu-sidebar-items/ecommerce-menu-sidebar-items.component";
import {
  EcommerceNavigationItemComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/components/ecommerce-navigation-item/ecommerce-navigation-item.component";
import {
  EditEcommerceNavigationItemComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/components/edit-ecommerce-navigation-item/edit-ecommerce-navigation-item.component";
import {
  NavigationMenuComponent
} from './websites/website/ecommerce/ecommerce-menu/navigation-menu/navigation-menu.component';
import {
  MenuTableRowComponent
} from "./websites/website/ecommerce/ecommerce-menu/navigation-menu/components/menu-table-row/menu-table-row.component";
import {
  MenusTableComponent
} from "./websites/website/ecommerce/ecommerce-menu/navigation-menu/components/menus-table/menus-table.component";
import {
  ProfessionalPlanFeaturesComponent
} from "./auth/checkout/professional-plan-features/professional-plan-features.component";
import {
  EcommerceMenuSearchComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/components/ecommerce-menu-search/ecommerce-menu-search.component";
import {
  NavigationInteractionsService
} from "./websites/website/ecommerce/ecommerce-menu/navigation-interactions.service";
import {SmoochAlternativeComponent} from "./smooch-alternative/smooch-alternative.component";
import {OutlineHandlerComponent} from "./outline-handler/outline-handler.component";
import {OutlineHandlerService} from "./outline-handler/outline-handler.service";
import {ScrollOnDragDirective} from "./outline-handler/scroll-on-drag.directive";
import {
  MakePrimaryDialogComponent
} from "./websites/website/hosting/domains/domain-item/make-primary-dialog/make-primary-dialog.component";
import {DomainsTableComponent} from "./websites/website/hosting/domains/domains-table/domains-table.component";
import {
  AddDomainListDialogComponent
} from "./websites/website/hosting/domains/add-domain-from-list/add-domain-list-dialog.component";
import {SubscriptionInfoComponent} from "./auth/checkout/subscription-info/subscription-info.component";
import {CheckoutBillingInfoComponent} from "./auth/checkout/billing-info/billing-info.component";
import {DsPhoneNumberComponent} from "./shared/components/ds-phone-number/ds-phone-number.component";
import {CalendarModule, CalendarMonthModule, DateAdapter} from 'angular-calendar';
import {adapterFactory} from "angular-calendar/date-adapters/date-fns";
import {
  AdditionalServiceCountComponent
} from "./auth/checkout/additional-service-count/additional-service-count.component";
import {CheckoutSummaryComponent} from "./auth/checkout/checkout-summary/checkout-summary.component";
import {ManageDnsListComponent} from "./websites/website/hosting/manage-dns/manage-dns-list/manage-dns-list.component";
import {
  AddDomainSuccessDialogComponent
} from "./websites/website/hosting/domains/add-domain-succes-dialog/add-domain-success-dialog.component";
import {
  AddCustomDomainSuccessDialogComponent
} from "./websites/website/hosting/domains/add-custom-domain-succes-dialog/add-custom-domain-success-dialog.component";
import {
  AddDomainZoneLimitExceededComponent
} from "./websites/website/hosting/domains/add-domain-zone-limit-exceeded/add-domain-zone-limit-exceeded.component";
import {
  HostingDomainsListComponent
} from "./websites/website/hosting/domains/hosting-domains-list/hosting-domains-list.component";
import {EcommerceMenuComponent} from "./websites/website/ecommerce/ecommerce-menu/ecommerce-menu.component";
import {
  AddEcommerceMenuComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/add-ecommerce-menu.component";
import {
  EcommerceMenuStructureComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/components/ecommerce-menu-structure/ecommerce-menu-structure.component";
import {
  EcommerceMenuSidebarComponent
} from "./websites/website/ecommerce/ecommerce-menu/add-ecommerce-menu/components/ecommerce-menu-sidebar/ecommerce-menu-sidebar.component";
import {DsTabHeadComponent} from "./shared/components/ds-tabs/tab-head/ds-tab-head.component";
import {
  CancelSubscriptionWithNoPaymentComponent
} from "./workspace/subscription-plan/dialogs/cancel-subscription-no-payments.component";
import {
  AddCustomDomainDialogComponent
} from "./websites/website/hosting/domains/add-domain-dialog/add-custom-domain-dialog.component";
import {NavigationService} from "./websites/website/ecommerce/ecommerce-menu/navigation.service";
import {
  DisconnectWebsiteAndMakeDialogComponent
} from "./websites/website/hosting/domains/domain-item/disconnect-website-make-primary-dialog/disconnect-website-make-primary-dialog.component";
import {CountdownService} from "./shared/services/countdown/countdown.service";
import {
  OneMonthFreeEcommerceComponent
} from "./shared/components/dialogs/one-month-free/components/one-month-free-ecommerce/one-month-free-ecommerce.component";
import {ShowMinimumPipe} from "./shared/pipes/show-minimum.pipe";
import {NgxIntlTelInputModule} from "ngx-intl-tel-input-gg";
import {EcommercePaymentMethodsComponent} from "./websites/website/ecommerce/payment-methods/payment-methods.component";
import {
  EcommercePaymentMethodActionComponent
} from "./websites/website/ecommerce/payment-methods/payment-method-action/payment-method-action.component";
import {WorkspaceResolver} from "./user/workspace-resolver";
import {BuilderV2Module} from "./builder-v2/builder-v2.module";
import {
  OneWeekFreeDemoEcommerceComponent
} from "./shared/components/dialogs/one-month-free/components/one-month-free-ecommerce/one-week-free-demo-ecommerce/one-week-free-demo-ecommerce.component";
import {
  OneWeekFreeDemoEcommerceOptionComponent
} from "./shared/components/dialogs/one-month-free/components/one-month-free-ecommerce/one-week-free-demo-ecommerce/one-week-free-demo-ecommerce__option/one-week-free-demo-ecommerce-option.component";
import {EcommercePaymentsIntroComponent} from "./websites/website/ecommerce/payments-intro/payments-intro.component";
import {EcommerceOverviewComponent} from "./websites/website/ecommerce/ecommerce-overview/ecommerce-overview.component";
import {EcommerceTaxComponent} from "./websites/website/ecommerce/ecommerce-tax/ecommerce-tax.component";
import {EcommerceEmailsComponent} from "./websites/website/ecommerce/ecommerce-emails/ecommerce-emails.component";
import {
  EcommerceChoosePaymentMethod
} from "./websites/website/ecommerce/dialogs/choose-payment-method/choose-payment-method.component";
import {
  TenWebPaymentsComponent
} from "./websites/website/ecommerce/payment-methods/tenweb-payments/tenweb-payments.component";
import {EcommercePaymentsService} from "./websites/website/ecommerce/ecommerce-payments.service";
import {SuccessfulPopupComponent} from "./websites/website/ecommerce/dialogs/success/successful-popup.component";
import {
  PayengineEnableDisableSwitchComponent
} from "./websites/website/ecommerce/dialogs/payengine-enable-disable/payengine-enable-disable.component";
import {LoadPayengineComponent} from "./websites/website/ecommerce/dialogs/load-payengine/load-payengine.component";
import {PaymentManagementComponent} from "./websites/website/ecommerce/payment-management/payment-management.component";
import {
  SinglePagePayoutComponent
} from "./websites/website/ecommerce/payment-management/single-page-payout/single-page-payout.component";
import {
  Activate10webPaymentsComponent
} from "./websites/website/ecommerce/activate-10web-payments/activate-10web-payments.component";
import {DsLineChartsComponent} from "./shared/components/ds-line-charts/ds-line-charts.component"; // Temporarily re-added for build
import {RefundItemsComponent} from "./websites/website/ecommerce/orders/refund-items/refund-items.component";
import {
  RefundItemsPopupComponent
} from "./websites/website/ecommerce/orders/refund-items-popup/refund-items-popup.component";
import {PercentChangePipe} from "./shared/pipes/percent-change.pipe";
// Material modules moved to specific feature modules that need them
import {DomainOfferComponent} from "./shared/components/dialogs/domain-offer/domain-offer.component";
import {MiddleEllipsisPipe} from "./shared/pipes/db-middleEllipsis.pipe";
import {ReplaceTextWithSpacesPipe} from "./shared/pipes/replace-text-with-spaces.pipe";
import {
  TestPaymentsDialogComponent
} from "./websites/website/ecommerce/payment-methods/test-payments-dialog/test-payments-dialog.component";
import {
  EmailsListTableComponent
} from "./websites/website/ecommerce/ecommerce-emails/emails-list-table/emails-list-table.component";
import {
  EmailsSettingsComponent
} from "./websites/website/ecommerce/ecommerce-emails/emails-settings/emails-settings.component";
// import {ColorPickerModule} from 'ngx-color-picker'; // Moved to feature modules where needed
import {
  EcommerceTaxMainComponent
} from "./websites/website/ecommerce/ecommerce-tax/tax-main/ecommerce-tax-main.component";
import {
  EcommerceTaxRateTableComponent
} from "./websites/website/ecommerce/ecommerce-tax/tax-rate-table/tax-rate-table.component";
import {
  EcommerceTaxSettingsComponent
} from "./websites/website/ecommerce/ecommerce-tax/tax-settings/tax-settings.component";
import {
  EnableTaxCalcComponent
} from "./websites/website/ecommerce/ecommerce-tax/enable-tax-calc/enable-tax-calc.component";
import {WoocommerceTaxPluginResolver} from "./websites/website/ecommerce/ecommerce-tax/woocommerce-tax-plugin-resolver";
import {
  EcommerceTaxPriceOptionsComponent
} from "./websites/website/ecommerce/ecommerce-tax/tax-price-options/tax-price-options.component";
import {AddTaxRateComponent} from "./websites/website/ecommerce/ecommerce-tax/add-tax-rate/add-tax-rate.component";
import {AddExpirationDatePipe} from "./shared/pipes/db-add-expiration-date.pipe";
import {RefundPriceComponent} from "./websites/website/ecommerce/orders/refund-price/refund-price.component";
import {RefundInfoComponent} from "./websites/website/ecommerce/orders/refund-info/refund-info.component";
import {WoopaymentsPluginResolver} from "./websites/website/ecommerce/payment-methods/woopayments-plugin-resolver";
import {
  ConfirmStripeConnectComponent
} from "./websites/website/ecommerce/dialogs/confirm-stripe-connect/confirm-stripe-connect.component";
import {SlideUpDirective} from "./shared/directives/slide-up/slide-up.directive";
import {
  EmailCampaignRegenerateComponent
} from "./shared/components/dialogs/email-campaign-regenerate/email-campaign-regenerate.component";
import {GoogleApplePayComponent} from "./shared/components/google-apple-pay/google-apple-pay.component";
import {SomethingWrongComponent} from "./shared/components/something-wrong/something-wrong.component";
import {DedicatedVmInfoComponent} from "./auth/checkout/dedicated-vm-info/dedicated-vm-info.component";
import {
  DedicatedVmContactComponent
} from "./components/common/dedicated-vm-contact-dialiog/dedicated-vm-contact.component";
import {CloningProgressComponent} from "./components/common/clone-website-progress/clone-progress.component";
import {GoogleAuthComponent} from "./auth/google-auth/google-auth.component";

import {LottieComponent, provideCacheableAnimationLoader, provideLottieOptions} from 'ngx-lottie';
import player from 'lottie-web';
import {
  ConfirmDowngradeAgreementComponent
} from "./websites/website/ecommerce/dialogs/confirm-downgrade-agreement/confirm-downgrade-agreement.component";
import {SinglePageDemoGuard} from "./single-page-demo/singlePageDemo.guard";
import {
  DemoProgressStepComponent
} from "./shared/components/dialogs/account-upgraded-dialog/demo-progress-step/demo-progress-step.component";
import {
  DemoProgressDoneComponent
} from "./shared/components/dialogs/account-upgraded-dialog/demo-progress-done/demo-progress-done.component";
import {
  SubscriptionPlanDetailsComponent
} from "./workspace/subscription-plan/alernative-subscription-plan/subscription-plan-details/subscription-plan-details.component";
import {
  CurrentSubPlanComponent
} from "./workspace/subscription-plan/alernative-subscription-plan/current-sub-plan/current-sub-plan.component";
import {
  ProductImagesComponent
} from "./websites/website/ecommerce/products/add-product/components/product-images/product-images.component";
import {
  MediaLibraryComponent
} from "./websites/website/ecommerce/products/add-product/components/media-library/media-library.component";
import {
  ProductVariantsComponent
} from "./websites/website/ecommerce/products/add-product/components/product-variants/product-variants.component";
import {
  AddProductVariantsModalComponent
} from "./websites/website/ecommerce/products/add-product/components/add-product-variants-modal/add-product-variants-modal.component";
import {
  ProductBatchEditComponent
} from "./websites/website/ecommerce/products/add-product/components/product-batch-edit/product-batch-edit.component";
import {ProductTaxComponent} from "./websites/website/ecommerce/products/add-product/product-tax/product-tax.component";
import {ClickOutsideDirective} from "./shared/directives/click-outside/click-outside.directive";
import {
  OrderAddressComponent
} from "./websites/website/ecommerce/orders/order-view/components/order-address/order-address.component";
import {
  OrderInfoComponent
} from "./websites/website/ecommerce/orders/order-view/components/order-info/order-info.component";
import {NgClickOutsideDirective} from "ng-click-outside2";
import {
  OneMonthFreeCheckoutDemoComponent
} from "./shared/components/dialogs/one-month-free/components/one-week-free-checkout-demo/one-month-free-checkout-demo.component";
import {
  OneWeekFreeDemoUpgradeComponent
} from "./shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {
  OneWeekFreeDemoPlanComponent
} from "./shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo__plan/one-week-free-demo-plan.component";
import {TextWordCountPipe} from "./shared/pipes/text-word-count.pipe";
import {VisitsWidgetComponent} from "./websites/website/overview/visits-widget/visits-widget.component";
import {
  ApiResellerTrialComponent
} from "./components/pages/10web-API/components/api-reseller-trial/api-reseller-trial.component";
import {ApiResellerProComponent} from "./shared/components/dialogs/api-reseller-pro/api-reseller-pro.component";
import {PrimarySidebarComponent} from "./shared/components/primary-sidebar/primary-sidebar.component";
import {
  PrimaryWorkspacePopupComponent
} from "./shared/components/primary-workspace-popup/primary-workspace-popup.component";
import {ProductHuntBannerComponent} from "./components/common/product-hunt-banner/product-hunt-banner.component";
import {MainPageHeaderComponent} from "./websites/website/overview/main-page-header/main-page-header.component";
import {LoadingDottedCircleComponent} from "./shared/components/loading-dotted-circle/loading-dotted-circle.component";
import {
  PricingPageViewComponent
} from "./shared/components/dialogs/one-month-free/components/pricing-page-view/pricing-page-view.component";
import {
  EcommercePlansComponent
} from "./shared/components/dialogs/one-month-free/components/pricing-page-view/ecommerce-plans/ecommerce-plans.component";
import {
  BusinessPlansComponent
} from "./shared/components/dialogs/one-month-free/components/pricing-page-view/business-plans/business-plans.component";
import {
  AgencyPlansComponent
} from "./shared/components/dialogs/one-month-free/components/pricing-page-view/agency-plans/agency-plans.component";
import {
  PricingPageCheckoutComponent
} from "./shared/components/dialogs/one-month-free/components/pricing-page-view/pricing-page-checkout/pricing-page-checkout.component";
import {
  HostedTrialStartedComponent
} from "./shared/components/dialogs/account-upgraded-dialog/hosted-trial-started/hosted-trial-started.component";
import {
  TrialLimitIndicatorComponent
} from "./shared/components/primary-sidebar/components/trial-limit-indicator/trial-limit-indicator.component";
import {
  TrialSubscriptionPlanComponent
} from "./workspace/subscription-plan/trial-subscription-plan/trial-subscription-plan.component";
import {
  TrialPlanDetailsComponent
} from "./workspace/subscription-plan/trial-subscription-plan/components/trial-plan-details/trial-plan-details.component";
import {
  TrialPlanSidebarComponent
} from "./workspace/subscription-plan/trial-subscription-plan/components/trial-plan-sidebar/trial-plan-sidebar.component";
import {
  CustomizeWithCopilotComponent
} from "./shared/components/dialogs/customize-with-copilot/customize-with-copilot.component";
import {AvailablePluginsResolver} from "./download-plugins/available-plugins.resolver";
import {NoWebsitesComponent} from "./websites/no-websites/no-websites.component";
import {InformativeBannerComponent} from "./components/common/informative-banner/informative-banner.component";
import {DsChartComponent} from "./shared/components/ds-chart/ds-chart.component";
import {CompanyInfoResolver} from "./shared/resolvers/company-info.resolver";
import {CheckoutResellerComponent} from "./auth/checkout/checkout-reseller/checkout-reseller.component";
import {EmailVerificationComponent} from "./account/email-verification/email-verification.component";
import {
    PricingPeriodSwitcherComponent
} from "./workspace/pricing/plan-period-switcher/pricing-period-switcher.component";
import {DbNumberParseFloatPipe} from "./shared/pipes/db-number-paresFloat.pipe";
import {
  VcImageUploadComponent
} from "./builder-v2/components/outline-generation/components/create-company-info/components/vc-image-upload/vc-image-upload.component";
import {
  UpdatePluginsDialogComponent
} from "./websites/website/overview/plugins-widget/update-plugins-dialog/update-plugins-dialog.component";

/*@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}
  handleError(error) {
    if (environment.production) {
      Sentry.captureException(error.originalError || error);
    }
    throw error;
  }
}*/

/*Raven
  .config('https://4867c7e4a3dc4562bdd6a80646f5f41f@sentry.io/269406', {
    tags: {
      git_commit: versions.revision
    },
    environment: environment.type
  })
  .install();*/

/*export class RavenErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    Raven.captureException(err);
  }
}*/
@Injectable()
export class DbHammerConfig extends HammerGestureConfig {
  override overrides = <any>{
    swipe: {direction: Hammer.DIRECTION_ALL},
  };
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {
  }

  handleError(error) {
    if (environment.production) {
      Sentry.captureException(error.originalError || error);
    }
    else {
      throw new Error(error);
    }
  }
}

// APP_INITIALIZER factory to load translations before app starts
// Using native fetch to avoid HTTP interceptor issues
export function initializeTranslations(): () => Promise<void> {
  return async (): Promise<void> => {
    try {
      // Load all translation files in parallel using native fetch (bypasses Angular interceptors)
        const [enData, itData, esData, deData, frData, nbData] = await Promise.all([
            fetch('assets/json/textMap_en.json').then(r => r.json()).catch(() => ({})),
            fetch('assets/json/textMap_it.json').then(r => r.json()).catch(() => ({})),
            fetch('assets/json/textMap_es.json').then(r => r.json()).catch(() => ({})),
            fetch('assets/json/textMap_de.json').then(r => r.json()).catch(() => ({})),
            fetch('assets/json/textMap_fr.json').then(r => r.json()).catch(() => ({})),
            fetch('assets/json/textMap_nb.json').then(r => r.json()).catch(() => ({}))
        ]);


        const maps = { en: enData, it: itData, es: esData, de: deData, fr: frData, nb: nbData };
      TextUtils.setTextMaps(maps);
    } catch (err) {
      console.error('Failed to load translations:', err);
      TextUtils.setTextMaps({ en: {}, it: {}, es: {}, de: {}, fr: {}, nb: {} });
    }
  };
}

@NgModule({
  declarations: [
    UpdatePluginsDialogComponent,
    DbDecimalPipe,
    DbNegativeNumberToZeoPipe,
    DbNumberParseIntPipe,
    BreakTextsPipe,
    ReplaceTextWithSpacesPipe,
    SearchByPropertyPipe,
    FilterByPropertyPipe,
    FilterByIntersectPipe,
    PercentChangePipe,
    TrimHomeUrlPipe,
    CountPipe,
    RoundNumberPipe,
    SplitBySpacePipe,
    DbDatePipe,
    ReplaceCommas,
    TimeFromNowPipe,
    PaginationPipe,
    StripTagsPipe,
    LimitPipe,
    MonthPipe,
    DurationPipe,
    SortProductsByFreeInstalledPipe,
    SortProductsByParentProductPipe,
    FilterProductByChildrenProductPipe,
    FilterProductsByStatePipe,
    AddZeroesPipe,
    TextWordCountPipe,
    TrimPipe,

    EmptySearchComponent,
    PageLoadingBarComponent,
    WaitingSpinnerComponent,
    ProgressSpinnerComponent,
    RatingStarsComponent,
    // ChartLinearComponent, // Moved to feature module

    ActivityNotificationsComponent,
    ActivityNotificationComponent,
    UserSummariesComponent,
    UserSummaryComponent,
    NotifierComponent,
    DialogWrapperComponent,
    UnavailableDialogComponent,
    DropdownListComponent,
    InfoTooltipComponent,
    InfoTooltipHoverComponent,
    ScoreInfoTooltipHoverComponent,
    TooltipShowAnimatedComponent,

    FcSearchInputComponent,
    FcDropdownSelectComponent,
    FcRadioButtonComponent,
    FcSwitchComponent,
    MainHeaderContentDirective,
    TooltipDirective,
    DropdownListDirective,
    ImageZoomDirective,
    PixelRatioDirective,
    TooltipComponent,
    ImageZoomComponent,
    SortIconComponent,
    RouteWrapperComponent,
    PaginationControlsComponent,
    DsPaginationControlsComponent,
    TabsComponent,
    TabHeadComponent,
    TabSubHeadComponent,
    TabContentComponent,
    AccordionComponent,
    ScheduleComponent,
    AccordionItemComponent,
    AccordionItemHeadComponent,
    AccordionItemContentComponent,

    ActionManagerComponent,
    ActionItemComponent,
    ActionProgressComponent,
    PageNotFoundComponent,
    SomethingWentWrongComponent,

    ConfirmDialogComponent,
    WebsiteCreationStatesDialogComponent,
    SmallScreenDisclaimerDialogComponent,
    MultisiteActionConfirmDialogComponent,
    ThirdPartyThemeActivationDialogComponent,
    DisconnectWebsiteAndMakeDialogComponent,
    ConfirmDomainDeleteDialogComponent,
    UploadCsvDialogComponent,
    AddCustomDomainDialogComponent,
    AddDomainListDialogComponent,
    AddDomainSuccessDialogComponent,
    AddCustomDomainSuccessDialogComponent,
    AddDomainZoneLimitExceededComponent,
    TestModeConfirmationComponent,
    BoosterUpdateComponent,
    NotifyDialogComponent,
    UpdateBoosterPluginComponent,
    WebPViaServerDialogComponent,
    EnableCloudflareDialogComponent,
    EditOriginalIpComponent,
    DisableCloudflareEnterpriseComponent,
    VerifyHostnameComponent,
    RemoveRedirectDialogComponent,
    AddRedirectDialogComponent,
    RemoveDnsDialogComponent,
    AddDnsDialogComponent,

    TxtContentDnsDialogComponent,
    GeneratePasswordComponent,
    ImportTemplateComponent,
    StartAiBuilderDialogComponent,
    AiBuilderNoticeDialogComponent,
    AiBuilderErrorDialogComponent,
    RemoveAllBackupsComponent,
    ChangePhpEngineComponent,
    EnableHttpsComponent,
    CustomHttpsComponent,
    AccountUpgradedDialogComponent,
    ApiResellerTrialComponent,
    AlreadyHaveAccountDialogComponent,
    ChangelogComponent,
    WhatsNewDialogComponent,
    ExtensionsComponent,
    PremiumPluginDialogComponent,
    FtpInvalidDialogComponent,
    ChangeEnvironmentDialogComponent,

    ProductItemComponent,
    DomainItemComponent,
    ExtensionItemComponent,
    PluginsComponent,
    ThemesComponent,
    OverviewComponent,
    PluginsWidgetComponent,
    WidgetProductItemComponent,
    IssueItemComponent,
    ProductItemScheduleComponent,
    ScoreWidgetComponent,
    SatisfactionSurveyWidgetComponent,
    UpgradeOfferWidgetComponent,

    DashboardComponent,
    CheckoutResellerComponent,
    BuilderSignoutComponent,
    MobileDisclaimerComponent,
    SignUpComponent,
    SignUpTrialComponent,
    LoginComponent,
    ContactUsComponent,
    LoginForgotComponent,
    StartAiWebsiteBuildingComponent,
    EmailPreferencesComponent,
    SettingsResetPasswordComponent,
    ChangeEmailComponent,
    ChangeEmailSuccessComponent,
    ResetPasswordSuccessComponent,
    AvailablePluginsComponent,
    AvailablePluginComponent,
    AvailableThemesComponent,
    AvailableThemeComponent,
    AddWebsiteChoiceComponent,
    AddWebsiteChoiceActivationFlowComponent,
    AddWebsiteChoiceActivationFlowComponent,
    MigrateOnlyDialogComponent,
    AddWebsiteChoiceActivationFlowComponent,
    MigrationDialogComponent,
    AutomaticMigrationComponent,
    WebsitesComponent,
    WebsiteComponent,
    WebsitePromptComponent,
    VoiceRecordingComponent,
    ChangeEnvironmentComponent,
    BackupLimitComponent,
    BoosterComponent,
    FrontEndComponent,
    OptimizeImagesComponent,
    OptimizeImagesComponentFree,
    SettingsComponent,
    CustomRulesComponent,
    ModesListComponent,
    BoosterAdvancedSettingsComponent,
    MainPageHeaderComponent,
    ImageOptimizerComponent,
    ImageOptimizationModesComponent,
    ImageOptimizerSectionComponent,
    ImageOptimizerWebpComponent,
    ImageMediaLibraryComponent,
    ImageOptimizerPagesComponent,
    ImageLimitsBannerComponent,
    ImageOptimizerComponent,
    WebsiteSidebarComponent,
    MainHeaderComponent,
    RechargeHeaderBannerComponent,
    AiBuilderHeaderBannerComponent,
    ProfilePopupComponent,
    AccountComponent,
    AccountSettingsComponent,
    ActivityLogComponent,
    ActivityLogFiltersComponent,
    SecuritySettingsComponent,
    BillingComponent,
    PaymentHistoryComponent,
    DbBillingInfoComponent,
    DbPlanLabelComponent,
    DbPlanLabelBoosterComponent,
    DbCdnInfoComponent,
    DbPaymentButtonComponent,
    WhiteLabelComponent,
    PaymentMethodItemComponent,
    AddPaymentMethodComponent,
    PaymentDialogComponent,
    ServiceUpgradeComponent,
    PaymentMethodComponent,
    SubscriptionPlanComponent,
    UpgradeBannerComponent,
    PlanPeriodSwitchComponent,
    PeriodSwitchComponent,
    DomainsSwitchComponent,
    PlanItemComponent,
    UpgradePlanComponent,
    BoosterWhiteLabelComponent,
    UpgradePlanItemComponent,
    PremiumPluginUpgradePlanItemComponent,
    BundleUpgradePlanItemComponent,
    CancelSubscriptionComponent,
    CancelSubscriptionWithNoPaymentComponent,
    RemoveCertificateComponent,
    InactiveWebsiteTroubleshootComponent,
    RemovePaymentMethodComponent,
    ChangePaymentMethodComponent,
    ResetPasswordComponent,
    WebsiteItemComponent,
    WebsiteItemListViewComponent,
    WebsiteItemMenuComponent,
    AddWebsiteComponent,
    CheckoutSummaryComponent,
    CheckoutPaymentInfoComponent,
    ApplyCouponCodeComponent,
    ProfessionalPlanCardsComponent,
    ProfessionalPlanFeaturesComponent,
    ProfessionalPlanInfoComponent,
    ChooseBundlePluginComponent,
    CheckoutPlanFeaturesComponent,
    CheckoutFormComponent,
    CheckoutPlanInfoComponent,
    CheckoutChangePlanComponent,
    PaypalWaitingComponent,
    GhostWaitingComponent,
    DomainsExceededComponent,
    GoogleScoreComponent,
    MigrationManualWordpressComponent,
    MigrationManualSummaryComponent,
    MigrationRequestSentDialogComponent,
    MigrationManualSubdomainComponent,
    MigrationManualStandardComponent,
    MigrationSubdomainHostingComponent,
    MigrationProgressComponent,
    PaypalButtonDirective,
    ComodoTrustLogoDirective,
    WpLinkDirective,
    CustomScrollbarDirective,
    BlockerScreenComponent,
    SeoComponent,
    RecreateWebsiteComponent,
    RecreateWebsiteStepsComponent,
    TemplatesComponent,
    SecurityComponent,
    TagItemComponent,
    AddTagDialogComponent,
    TagsListDialogComponent,
    RemoveTagDialogComponent,
    PgWelcomePerformanceDialogComponent,
    FromManagerDialogComponent,
    ManualOptimizationDialogComponent,
    AddWebsiteDialogComponent,
    GrantAccessCopyWebsiteDialogComponent,
    RestoreSecurityIssueDialogComponent,
    InstallWebsiteComponent,
    AddWebsiteManuallyComponent,
    WordpressInstallationComponent,
    WebsiteInstallTrialFlowComponent,
    SubwebsiteInstallComponent,
    HostingComponent,
    HostingInfoComponent,
    HostingDomainsComponent,
    HostingDomainsListComponent,
    HostingDomainsManageDNSComponent,
    HostingDomainsManageAllComponent,
    HostingToolsComponent,
    HostingRedirectsComponent,
    HostingManageEnvironmentComponent,
    HostingLogsComponent,
    HostingEnvironmentComponent,
    HostingResourceUsageComponent,
    PushToLiveConfirmationComponent,
    InfoTooltipDirective,
    InfoTooltipHoverDirective,
    TooltipShowAnimatedDirective,
    WebsiteActionSuccessComponent,
    WebsiteCopyCongratsDialogComponent,
    HostingOfferBannerComponent,
    TrialInvitedBannerComponent,
    TenWebXComponent,
    AdvancedToolsComponent,
    CouponApplyResponseDialogComponent,
    MoveTo10WebComponent,
    OnboardingComponent,
    SetupComponent,
    CreateComponent,
    OptimizeComponent,
    StartBoosterComponent,
    CreateOptionsComponent,
    OtherStepComponent,
    QuestionStepComponent,
    MoveHostingComponent,
    RecreateAiComponent,
    ChangeHostingComponent,
    MySitesComponent,
    Enable2faComponent,
    Disable2faComponent,
    TwoFaVerificationComponent,
    HowToVideosComponent,
    TourComponent,
    AdditionalWebsiteComponent,
    AdditionalServiceCountComponent,
    AdditionalHostingAddonComponent,
    AdditionalHostingAddonInfoComponent,
    VideoDialogComponent,
    TwoFADialogComponent,
    WebsiteAvailableDialogComponent,
    GoogleInsightsScoreDialogComponent,
    SSlFailedDialogComponent,
    MobileAvailableComponent,
    TestPaymentsDialogComponent,
    RechargeComponent,
    WebsiteTitleComponent,
    MigrateWebsiteLampComponent,
    NotificationDialogComponent,
    LoadingWorkspaceComponent,
    LoadingLineComponent,
    WorkspacePopupComponent,
    UserManagementComponent,
    LeaveRemoveWorkspaceDialogComponent,

    WorkspaceComponent,
    WorkspaceSettingsComponent,
    BillingDetailsComponent,
    ManageWorkspaceUsersDialogComponent,
    WorkspacePopupFirstTimeComponent,

    AddOwnWorkspaceComponent,
    OptimizerLoadingComponent,
    MoveTo10webConnectedComponent,
    MoveTo10webHostedComponent,
    CheckoutPlanFeaturesComponent,
    ChooseBundlePluginComponent,
    ApplyCouponCodeComponent,
    ProfessionalPlanCardsComponent,
    ProfessionalPlanFeaturesComponent,
    ProfessionalPlanInfoComponent,
    CheckoutSummaryComponent,
    CheckoutPaymentInfoComponent,
    CheckoutFormComponent,
    CheckoutPlanInfoComponent,
    CheckoutChangePlanComponent,
    MigrationStepsComponent,
    WebsiteInstallLoadingDialogComponent,
    MigrationSuccessDialogComponent,

    NotificationDialogComponent,
    ZoomInDialogComponent,
    AgencyPartnerProgramDialogComponent,
    LoadedDirective,
    MigrateOnlyComponent,
    OptimizeWithMigrateComponent,
    CreateMigrateComponent,
    ConnectOnlyComponent,
    FirstTimeHighlightComponent,
    FirstTimeHighlightDirective,
    DsProgressBarComponent,
    OptimizationPreviewComponent,
    UrlStepComponent,
    CompareStepComponent,
    OptimizeStepComponent,
    DsDropdownListComponent,
    AddOwnWorkspaceDialogComponent,
    HolidayOfferBannerComponent,
    WebsitesListComponent,
    DsBreadcrumbsComponent,
    DsChartComponent,
    DsMobilePageTitleComponent,
    DsMultiselectComponent,
    CarouselComponent,
    PaymentInfoComponent,
    GoogleApplePayComponent,
    DsScoreComponent,
    DsCircleComponent,
    PlatformAccountUpgradedComponent,
    EcommerceCompleteSetupComponent,
    DemoProgressStepComponent,
    DemoProgressDoneComponent,
    HostedTrialStartedComponent,
    CustomizeWithCopilotComponent,
    BoosterProUpgradedComponent,
    SlackCommunityComponent,
    InformativeBannerFeatureComponent,
    EnvSwitcherComponent,
    ActivitiesListComponent,
    ActivitiesTextPipe,
    ActivitiesListComponent,
    CreateWebsiteComponent,
    CloneWebsiteComponent,
    CloneInstallationComponent,
    OneProdBundleOfferBannerComponent,
    WebsiteItemOverlayComponent,
    WebsiteItemActionsComponent,
    FilterPipe,
    NoWebsitesComponent,
    BoosterGeneralComponent,
    EnableCdnComponent,
    BoosterOptimizationStatusComponent,
    NewVersionAvailableComponent,
    OptimizationLevelsComponent,
    BoosterPageScoreComponent,
    BoosterPageOptimizerComponent,
    SubscriptionBlockWithPagesComponent,
    OptimizeWithBoosterProComponent,
    BoosterFeaturesListComponent,
    BoosterFeaturesListMobileComponent,
    OptimizationDialogComponent,
    InformativeDialogComponent,
    OptimizationProgressComponent,
    ApiUnavailableComponent,
    PlanNotSupportedComponent,
    OptimizationSuccessComponent,
    RemoveBoostedPageDialogComponent,
    MigrateForBoosterComponent,
    BoosterFeaturesListPaidComponent,
    NotificationNoticesComponent,
    CdnNoticeComponent,
    CdnEnableBannerComponent,
    BackEndComponent,
    OptimizationWithHostingComponent,
    BoosterProComponent,
    AdvancedSettingsComponent,
    BoosterProTempComponent,
    BenchmarkComponent,
    BenchmarkTableComponent,
    DsAccordionComponent,
    BackEndFeaturesComponent,
    BoosterPluginDownloadDialogComponent,
    BenchmarkResultComponent,
    BoosterFeatureStatusComponent,
    BoosterTroubleInstructionsComponent,
    BoosterDisconnectedComponent,
    CreateAiStepsComponent,
    CreateAiProcessComponent,
    AiBuilderComponent,
    AiBuilderDisabledComponent,
    AiBuilderPagesComponent,
    AiBuilderPagesTableComponent,
    AiBuilderPageItemComponent,
    AiBuilderCreatePageComponent,
    AiBuilderCreatePageOptionsComponent,
    AiBuilderCreatePageBuilderComponent,
    AiBuilderCreatePageBlankComponent,
    AiBuilderCreatePagePreviewDemoComponent,
    AiBuilderCreatePageTemplatesComponent,
    AnniversaryOfferComponent,
    AnniversaryOfferBannerComponent,
    CountdownPipe,
    WebsitesControlsComponent,
    WebsitesControlsCardComponent,
    OneMonthFreeComponent,
    OneMonthFreeFeaturesComponent,
    OneMonthFreeBuilderComponent,
    OneWeekFreeBuilderMobileComponent,
    OneMonthFreeEcommerceComponent,
    OneMonthFreeDemoComponent,
    OneWeekFreeDemoComponent,
    OneWeekFreeDemoUpgradeComponent,
    OneWeekFreeDemoPlanComponent,
    OneWeekFreeDemoEcommerceComponent,
    OneWeekFreeDemoEcommerceOptionComponent,
    PricingPageViewComponent,
    EcommercePlansComponent,
    BusinessPlansComponent,
    AgencyPlansComponent,
    PricingPageCheckoutComponent,
    OneMonthFreeHireExpertComponent,
    OneMonthFreePlansComponent,
    OneMonthFreeSelectPlanComponent,
    OneWeekFreeDemoOptionComponent,
    BoosterProSelectPlanComponent,
    OneMonthFreeCheckoutComponent,
    OneMonthFreeCheckoutDemoComponent,
    OneMonthFreeEditorBannerComponent,
    OneMonthFreeCheckoutInfoComponent,
    OneMonthFreePaymentStepsComponent,
    DomainOfferComponent,
    EmailCampaignRegenerateComponent,
    SuccessAfterDemoUpgradeComponent,
    PaidFeatureUpgradeComponent,
    PaidFeatureUpgradeCheckoutComponent,
    PaidFeatureUpgradeBannerComponent,
    CreateWebsiteOptionsComponent,
    CreateWebsiteInstallComponent,
    CreateWebsiteInstallCloneComponent,
    CreateWebsiteInstallBlankComponent,
    CreateWebsiteInstallFooterComponent,
    CreateWebsiteInstallWebsitesListComponent,
    SwitchPlanComponent,
    SwitchPlanAnnualComponent,
    SwitchPlanCheckoutComponent,
    DownloadManagerRecreateComponent,
    DownloadManagerConnectComponent,
    InformativeBannerComponent,
    LimitExceededComponent,
    MigrateWebsiteComponent,
    MigrateWebsiteFooterComponent,
    ManualMigrateWebsiteComponent,
    ManualMigrateWebsiteSubdomainComponent,
    ManualMigrateWebsiteHostingCredentialsComponent,
    ManualMigrateWebsiteDetailsComponent,
    ManualMigrateWebsiteSummaryComponent,
    ManualMigrateWebsiteSummaryDetailsComponent,
    ManualMigrateWebsiteRequestSentComponent,
    CreateSubSiteComponent,
    CreateWebsiteSuccessComponent,
    CreateWebsiteSuccessScoreComponent,
    WebsitesControlsDetailedComponent,
    WebsitesEmptyDedicatedHostingComponent,
    WebsitesControlsDetailedCardComponent,
    UpgradeTenWebProComponent,
    CreateWebsiteTrialComponent,
    CreateWebsiteTrialInstallComponent,
    BoosterImageOptimizerComponent,
    CDNComponent,
    CdnPendingComponent,
    PaidFeatureUpgradeFeaturesComponent,
    UpgradeBoosterProComponent,
    PaidFeatureUpgradePlansComponent,
    CdnDisabledComponent,
    CdnTestComponent,
    CdnOverviewComponent,
    CdnFullPageCacheComponent,
    CdnFullPageCacheToggleComponent,
    CdnFullPageCacheSettingsComponent,
    CdnSettingsComponent,
    CdnDisableEnterpriseComponent,
    CdnNoticeSectionComponent,
    CdnOriginalServerComponent,
    CdnFeatureStackComponent,
    CdnClearCacheComponent,
    CdnGeneralComponent,
    RemoveWebsiteComponent,
    PercentOfferComponent,
    ProductHuntBannerComponent,
    PercentOfferBannerComponent,
    PercentOfferCounterComponent,
    ConnectAndOptimizeComponent,
    PercentOfferCounterComponent,
    DsFilterChipsComponent,
    CreateAiAssistantComponent,
    CreateAiAssistantChooseWebsiteTypeComponent,
    CreateAiBuilderComponent,
    CreateAiAssistantFormViewComponent,
    CreateAiAssistantStartViewComponent,
    CreateAiAssistantFinalizeComponent,
    RecreateFirstTimeComponent,
    NotificationModalComponent,
    RemoveDemoComponent,
    LiveChatAvailableComponent,
    SignUpWithHubspotComponent,
    CreateAiAssistantStoreTypeComponent,
    CreateAiAssistantCompanyTypeComponent,
    CreateAiAssistantTemplateComponent,
    CreateAiAssistantTemplateSelectComponent,
    CreateAiAssistantThemesComponent,
    GenerationFailedComponent,
    DemoProgressComponent,
    SwitchAnnualDialogComponent,
    BoosterActivationBannerComponent,
    ImageOptimizerSuccessComponent,
    BoosterFreePlanIntroComponent,
    MigrationRestApiBlockedComponent,
    OptimizedWebsitesCardComponent,
    ApplyProUpgradeDialogComponent,
    SiteLockIsOnComponent,
    DeleteAccountDialogComponent,
    AccountDeletedComponent,
    // Ecommerce Start
    EcommerceComponent,
    ProductsComponent,
    ProductsTableComponent,
    ProductTableRowComponent,
    AddProductComponent,
    ProductActionsHeaderComponent,
    ProductDescriptionComponent,
    ProductMediaComponent,
    ProductMainPricingComponent,
    ProductPriceVariantsComponent,
    ProductInventoryComponent,
    ProductLinkedProductsComponent,
    ProductShippingComponent,
    ProductCategoryComponent,
    DsChipInputComponent,
    FormControlPipe,
    ProductVariantsDialogComponent,
    RemoveItemDialogComponent,
    CurrencyMaskDirective,
    GenerateWithAiComponent,
    OrdersComponent,
    OrdersTableComponent,
    OrderTableRowComponent,
    OrderViewComponent,
    RefundItemsComponent,
    RefundItemsPopupComponent,
    EcommerceViewBodyComponent,
    EcommerceViewHeaderComponent,
    EcommerceViewSidebarComponent,
    EcommerceViewStatsComponent,
    EcommerceItemViewComponent,
    EcommercePaymentMethodsComponent,
    EcommercePaymentMethodActionComponent,
    EcommerceChoosePaymentMethod,
    ConfirmStripeConnectComponent,
    ConfirmDowngradeAgreementComponent,
    EcommercePaymentsIntroComponent,
    EcommerceOverviewComponent,
    EcommerceTaxComponent,
    EcommerceTaxMainComponent,
    EcommerceTaxRateTableComponent,
    EcommerceTaxPriceOptionsComponent,
    EcommerceTaxSettingsComponent,
    AddTaxRateComponent,
    RefundPriceComponent,
    RefundInfoComponent,
    EnableTaxCalcComponent,
    EcommerceEmailsComponent,
    LoadPayengineComponent,
    TenWebPaymentsComponent,
    CustomersComponent,
    CustomersTableComponent,
    CustomersTableRowComponent,
    CustomerViewComponent,
    ProductsListComponent,
    OrdersListComponent,
    CustomersListComponent,
    DateRangePickerComponent,
    CustomHeaderComponent,
    CustomRangePanelComponent,
    // Ecommerce End
    AccountDeletedComponent,
    AlternativeSubscriptionPlanComponent,
    DsPaymentButtonComponent,
    DsCdnInfoComponent,
    DsBillingInfoComponent,
    SiteLockIsOnComponent,
    DsPlanInfoComponent,
    EcommerceComponent,
    EcommerceDisabledComponent,
    QuillEditorComponent,
    NumberOnlyDirective,
    ManualMigrationDialogComponent,
    MigrationZipUploadComponent,
    MigrationProgressDialogComponent,
    ProfessionalPlanCardsComponent,
    ProfessionalPlanInfoComponent,
    ProfessionalPlanFeaturesComponent,
    EcommerceSettingsComponent,
    EcommerceMenuComponent,
    AddEcommerceMenuComponent,
    EcommerceMenuStructureComponent,
    EcommerceMenuSidebarComponent,
    EcommerceMenuSidebarItemsComponent,
    EcommerceNavigationItemComponent,
    EditEcommerceNavigationItemComponent,
    NavigationMenuComponent,
    MenuTableRowComponent,
    MenusTableComponent,
    EcommerceMenuSearchComponent,
    SmoochAlternativeComponent,
    OutlineHandlerComponent,
    ScrollOnDragDirective,
    MakePrimaryDialogComponent,
    DomainsTableComponent,
    CheckoutBillingInfoComponent,
    SubscriptionInfoComponent,
    DsPhoneNumberComponent,
    DsLineChartsComponent, // Temporarily re-added for build
    ManageDnsListComponent,
     SuccessfulPopupComponent,
    PayengineEnableDisableSwitchComponent,
    PaymentManagementComponent,
    Activate10webPaymentsComponent,
    SinglePagePayoutComponent,
    EmailsListTableComponent,
    EmailsSettingsComponent,
    ProductImagesComponent,
    MediaLibraryComponent,
    ProductVariantsComponent,
    AddProductVariantsModalComponent,
    ProductBatchEditComponent,
    ProductTaxComponent,
    EmailsSettingsComponent,
    DedicatedVmInfoComponent,
    DedicatedVmContactComponent,
    EmailsSettingsComponent,
    CloningProgressComponent,
    SubscriptionPlanDetailsComponent,
    CurrentSubPlanComponent,
    CloningProgressComponent,
    OrderAddressComponent,
    OrderInfoComponent,
    PrimarySidebarComponent,
    PrimaryWorkspacePopupComponent,
    TrialLimitIndicatorComponent,
    TrialSubscriptionPlanComponent,
    TrialPlanDetailsComponent,
    TrialPlanSidebarComponent,
    EmailVerificationComponent,
  ],
  bootstrap: [DashboardComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    HttpClientJsonpModule,
    HttpClientModule,
    BrowserModule,
    FormsModule,
    NgxIntlTelInputModule, // Keep for main authentication flows
    ReactiveFormsModule,
    GravatarDirective,
    NgxChartsModule, // Temporarily re-added for build
    NgChartsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatButtonModule,
    CommonModule,
    CookieModule.forRoot(),
    DashboardRoutingModule,
    DbStoreModule,
    // ColorPickerModule, // Move to feature modules where used
    StoreModule.forRoot(fromStore.reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false
      }
    }),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot(fromStore.effects),
    DashboardBootstrapModalModule,
    // MalihuScrollbarModule.forRoot(),
    NouisliderModule, // Temporarily re-added for build
    BrowserAnimationsModule,
    ClipboardModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    NgxCaptchaModule, // Keep for main auth flows
    NgMultiSelectDropDownModule.forRoot(), // Move to feature modules where used
    DbModalModule,
    DbDatepickerModule,
    CalendarMonthModule,
    DragulaModule,
    DsTooltipDirective,
    LoadingCircularSpinnerComponent,
    LoadingScreenComponent,
    LoadingSpinnerComponent,
    LoadingDottedCircleComponent,
    DsDropdownComponent,
    DbTextPipe,
    DbReplaceTextPipe,
    DbSafePipe,
    SafeHtmlPipe,
    SizePipe,
    WordByCountPipe,
    DbFormatDatePipe,
    SortByPipe,
    AsyncPipe,
    BuilderV2Module,
    DsTabsComponent,
    DsTabHeadComponent,
    DsTabContentComponent,
    AiBuilderDemoBannerComponent,
    LowerCaseSpecificWordsPipe,
    MiddleEllipsisPipe,
    MainSidebarEmptyComponent,
    SlideUpDirective,
    SomethingWrongComponent,
    CreateAiAssistantSignUpComponent,
    LoadingBarComponent,
    PasswordValidationComponent,
    TrustPilotComponent,
    LottieComponent,
    GoogleAuthComponent,
    SlideUpDirective,
    ClickOutsideDirective,
    ClickOutsideDirective,
    NgClickOutsideDirective,
    DsSearchInputComponent,
    ApiResellerProComponent,
    DbNumberFormatPipe,
    PricingPeriodSwitcherComponent,
    DsSearchInputComponent,
    VisitsWidgetComponent,
    ProgressBarComponent,
    ShowMinimumPipe,
    DbNumberParseFloatPipe,
    VcImageUploadComponent,

  ],
  exports: [
    TooltipDirective,
    TrimPipe,
  ],
  providers: [
    // Load translations before app initialization (using native fetch to bypass interceptors)
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [],
      multi: true
    },
    DbDecimalPipe,
    DbNegativeNumberToZeoPipe,
    DbTextPipe,
    DbReplaceTextPipe,
    WordByCountPipe,
    SafeHtmlPipe,
    BreakTextsPipe,
    ReplaceTextWithSpacesPipe,
    SearchByPropertyPipe,
    SortByPipe,
    FilterByPropertyPipe,
    FilterByIntersectPipe,
    SizePipe,
    TrimHomeUrlPipe,
    CountPipe,
    SplitBySpacePipe,
    DbDatePipe,
    ReplaceCommas,
    TimeFromNowPipe,
    PaginationPipe,
    StripTagsPipe,
    LimitPipe,
    MonthPipe,
    DurationPipe,
    SortProductsByFreeInstalledPipe,
    SortProductsByParentProductPipe,
    FilterProductByChildrenProductPipe,
    FilterProductsByStatePipe,
    DbSafePipe,
    LowerCaseSpecificWordsPipe,
    DatePipe,
    FilterPipe,
    ShowMinimumPipe,
    MiddleEllipsisPipe,
    DbFormatDatePipe,
    AddExpirationDatePipe,
    {
      provide: ErrorHandler,
      useClass: ErrorHandler
    },
    GlobalEventDispatcherService,
    RouteUrlHistoryService,
    RouteDataService,
    TitleService,
    PageLoadingBarService,
    GoogleAnalyticsService,
    RequesterService,
    MainHeaderService,
    CountdownService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServiceKeyInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FormDataInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DbResponseInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DemoRequestsInterceptor,
      multi: true
    },
    // {provide: ErrorHandler, useClass: SentryErrorHandler},
    SequenceGuard,
    AuthService,
    ExternalApiService,
    AuthGuard,
    SetupOnboardingRedirectGuard,
    SinglePageDemoGuard,
    StarterGuard,
    SignUpGuard,
    BuilderSignoutGuard,
    SignUpTrialGuard,
    SubscriptionUpgradeAvailableGuard,
    WebsitesGuard,
    RequestRepeaterService,
    UserService,
    WorkspaceService,
    UserStateGuard,
    UserHostingServicesGuard,
    UserPlanTypeGuard,
    ActivityNotificationService,
    WebsiteService,
    ProductService,
    ProductStatusActionService,
    ProductActionService,
    BuilderTemplatesService,
    BackupService,
    PerformanceService,
    SecurityService,
    NotificationDialogService,
    UserResolver,
    CompanyInfoResolver,
    BoosterTroubleshootResolver,
    UserSubscriptionsResolver,
    UserHostingSubscriptionsResolver,
    AvailableSubscriptionsResolver,
    UserSubscriptionResolver,
    PaymentMethodsResolver,
    UserPaymentMethodResolver,
    PaymentsResolver,
    EmailPreferencesResolver,
    WebsiteGuard,
    WebsiteResolver,
    WebsiteSettingsResolver,
    PluginsResolver,
    ManagerPluginResolver,
    BackupPluginResolver,
    BackupsResolver,
    BackupDataLimitResolver,
    ImageOptimizerPluginResolver,
    SeoPluginResolver,
    WoocommerceTaxPluginResolver,
    WoopaymentsPluginResolver,
    ElementorPluginResolver,
    BuilderPluginResolver,
    LastImportedTemplateResolver,
    SecurityPluginResolver,
    SecurityScanResolver,
    SecurityIgnoredIssuesResolver,
    ThemesResolver,
    HostingService,
    HostingInfoResolver,
    HostingCertificateResolver,
    HostingDomainsResolver,
    HostingDomainsManageDnsResolver,
    HostingRedirectsResolver,
    HostingLogsResolver,
    HostingResourceUsageByWebsiteResolver,
    HostingResourceVisitorsByWebsiteResolver,
    HostingVisitsByWebsiteByDateResolver,
    HostingResourceUsageResolver,
    HostingResourceVisitorsResolver,
    HostingResourceUsageResolver,
    HostingDomainsManageAllResolver,
    OverviewOrderingResolver,
    UserManagementService,
    HostingToolsResolver,
    AddWebsiteResolver,
    AvailablePluginsResolver,
    UserLocalStorageResolver,
    UserManagementResolver,
    BillingInfoResolver,
    ActivityLogResolver,
    ActivityLogDeletedDomainsResolver,
    ActivityLogDeletedClientsResolver,
    BenchmarkResolver,
    WoocommercePluginResolver,
    WorkspaceResolver,
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: DbHammerConfig,
    },
    DbActiveModal,
    AccountService,
    EcommerceService,
    EcommercePaymentsService,
    FormControlPipe,
    ProductAddHelperService,
    OrdersService,
    CustomersService,
    AiBuilderCommonService,
    EcommerceSettingsService,
    NavigationService,
    NavigationInteractionsService,
    OutlineHandlerService,
    ProgressBarComponent,
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideLottieOptions({player: () => player}),
    provideCacheableAnimationLoader(),
  ]
})
export class DashboardModule {

  constructor(
    private _routeUrlSegmentsHistoryService: RouteUrlHistoryService,
    private _routeDataService: RouteDataService,
    private _titleService: TitleService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _googleAnalytics4Service: GoogleAnalytics4Service, // Non-blocking GA4 initialization
    private _requestRepeaterService: RequestRepeaterService,
    private _authService: AuthService
  ) {
    // GoogleAnalytics4Service automatically initializes in background when company info loads
  }

}
